export type PillarSection = {
  title: string;
  description: string;
  bullets: string[];
};

export type PillarPage = {
  id: string;
  slug: string;
  path: string;
  title: string;
  subtitle: string;
  description: string;
  heroImage: string;
  problem: PillarSection;
  notion: PillarSection;
  longForm: {
    intro: string[];
    sections: Array<{
      title: string;
      paragraphs: string[];
      bullets?: string[];
    }>;
    templateLinkIds: string[];
    articleLinkSlugs: string[];
  };
  templateIds: string[];
  articleSlugs: string[];
  seo: {
    title: string;
    description: string;
    keywords: string[];
    openGraph: {
      title: string;
      description: string;
      type: string;
      image: string;
    };
  };
  customCta?: {
    title: string;
    description: string;
    buttonText: string;
    contactSubject: string;
  };
  /** CTA hero personnalisé (remplace les liens par défaut) */
  heroCta?: {
    primary: { label: string; to: string };
    secondary?: { label: string; to: string };
  };
  /** Pages pilier liées (maillage interne SEO) */
  relatedPillarPaths?: string[];
  category?: 'thematique' | 'metier';
};

export const PILLAR_PAGES: PillarPage[] = [
  {
    id: 'entreprise',
    slug: 'entreprise',
    path: '/notion-entreprise',
    title: 'Notion pour les entreprises',
    subtitle:
      'Des pages stables pour structurer conformité, opérations et pilotage.',
    description:
      'Centralisez vos processus critiques et créez un référentiel durable pour vos équipes.',
    heroImage: '/images/templates/conformite_ai_act_reglement_europeen_ia.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les équipes s’appuient sur des fichiers dispersés, des outils hétérogènes et des preuves difficiles à retrouver.',
      bullets: [
        'Processus fragmentés entre services',
        'Manque de traçabilité sur les décisions',
        'Documentation qui vieillit trop vite'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion devient le socle unique pour piloter, documenter et partager ce qui compte vraiment.',
      bullets: [
        'Bases de données reliées et actionnables',
        'Mise à jour collaborative en temps réel',
        'Visibilité claire sur les priorités'
      ]
    },
    longForm: {
      intro: [
        "Les entreprises grandissent vite, mais leurs systèmes d'information restent souvent fragmentés. Les équipes se débrouillent entre des tableurs, des dossiers partagés et des outils qui ne parlent pas entre eux. Résultat : une perte de temps, des décisions prises sans visibilité et des audits compliqués.",
        "Une page pilier entreprise sert de socle éditorial : elle explique les enjeux, montre les bénéfices d'une approche structurée et renvoie vers les bons templates et articles. Le but est simple : créer un hub SEO durable, qui attire des lecteurs qualifiés pendant des années."
      ],
      sections: [
        {
          title: 'Structurer les opérations',
          paragraphs: [
            "La performance opérationnelle dépend moins de l'outil que de la clarté des process. Notion permet d'unifier la documentation, les checklists et la planification dans un espace partagé. Les équipes évitent les doublons et disposent enfin d'un référentiel vivant.",
            "Pour les activités terrain (hôtellerie, chantiers, copropriété), un tableau de bord visuel facilite le suivi quotidien. Les données sont reliées, ce qui réduit le besoin de ressaisie et donne une vision fiable des priorités."
          ],
          bullets: [
            'Réduction des frictions entre équipes',
            'Processus reproductibles et documentés',
            'Pilotage temps réel des opérations'
          ]
        },
        {
          title: 'Sécuriser la conformité',
          paragraphs: [
            "Les obligations réglementaires augmentent : CSRD, AI Act, bilans carbone, audits internes. Sans système, les preuves sont dispersées et l'effort de conformité devient disproportionné.",
            "Notion sert de coffre-fort organisationnel. Chaque exigence devient une base de données, chaque preuve est reliée à son indicateur, et chaque décision reste traçable dans le temps."
          ]
        },
        {
          title: 'Installer une gouvernance durable',
          paragraphs: [
            "Une entreprise a besoin de mémoire : décisions, comptes rendus, indicateurs, risques. En structurant ces informations, les dirigeants gagnent en clarté et réduisent la dépendance aux personnes clés.",
            "C'est ce qui transforme Notion en outil de gouvernance, pas seulement en espace de notes. Les équipes savent où chercher, et les responsables savent où piloter."
          ]
        }
      ],
      templateLinkIds: [
        'conformite-csrd',
        'conformite-ai-act-reglement-europeen-ia',
        'bilan-carbone-entreprise-beges-ghg-protocol',
        'gestion-chantiers-reunions-commissions'
      ],
      articleLinkSlugs: [
        'ai-act-gouvernance-ia-b2b',
        'csrd-bilan-carbone-notion',
        'gestion-hoteliere-ops'
      ]
    },
    templateIds: [
      'conformite-csrd',
      'conformite-ai-act-reglement-europeen-ia',
      'bilan-carbone-entreprise-beges-ghg-protocol',
      'gestion-chantiers-reunions-commissions',
      'gestion-copropriete-france-syndic',
      'gestion-hoteliere-complete'
    ],
    articleSlugs: [
      'ai-act-gouvernance-ia-b2b',
      'csrd-bilan-carbone-notion',
      'gestion-hoteliere-ops',
      'pilotage-copropriete-chantiers',
      'gestion-association-1901-b2b',
      'qualiopi-pilotage-formation-b2b'
    ],
    seo: {
      title: 'Notion Entreprise | Structurer conformité & opérations',
      description:
        'Page pilier pour les entreprises : enjeux, solutions Notion, templates liés et articles de référence.',
      keywords: [
        'notion entreprise',
        'pilotage',
        'conformité',
        'opérations',
        'gouvernance'
      ],
      openGraph: {
        title: 'Notion pour les entreprises',
        description:
          'Structurez vos obligations et vos opérations avec Notion.',
        type: 'website',
        image: '/images/templates/conformite_ai_act_reglement_europeen_ia.jpg'
      }
    },
    category: 'thematique'
  },
  {
    id: 'conformite',
    slug: 'conformite',
    path: '/notion-conformite',
    title: 'Notion pour la conformité',
    subtitle:
      'Centralisez vos obligations, preuves et audits sans friction.',
    description:
      'Un hub SEO pour la conformité CSRD, AI Act, Qualiopi et reporting ESG.',
    heroImage: '/images/templates/csrd_compliance.jpg',
    problem: {
      title: 'Le problème',
      description:
        'La conformité exige des preuves solides, mais les informations restent dispersées et difficiles à auditer.',
      bullets: [
        'Multiplication des normes et indicateurs',
        'Preuves stockées dans des silos',
        'Manque de visibilité sur les audits'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion transforme vos obligations en un système documenté, clair et prêt pour les audits.',
      bullets: [
        'Checklists et preuves centralisées',
        'Reporting ESG et suivi des actions',
        'Gouvernance partagée et traçable'
      ]
    },
    longForm: {
      intro: [
        "La conformité n'est plus un sujet réservé aux grands groupes. PME, cabinets et structures spécialisées doivent désormais prouver leurs engagements, documenter leurs processus et produire des indicateurs fiables.",
        "Une page pilier conformité a pour rôle d'expliquer ces enjeux, d'illustrer la valeur de Notion et d'orienter les lecteurs vers des templates opérationnels. C'est un contenu de référence qui reste valable plusieurs années."
      ],
      sections: [
        {
          title: 'Rendre la conformité pilotable',
          paragraphs: [
            "Quand la conformité repose sur des fichiers isolés, les équipes passent leur temps à chercher, vérifier et reconstruire. Notion centralise les exigences, les preuves et les actions correctives dans un espace unique.",
            "Chaque indicateur devient un élément traçable. On sait qui a fait quoi, quand et pourquoi. Cela sécurise l'organisation et réduit la pression avant un audit."
          ]
        },
        {
          title: 'Transformer l’obligation en avantage',
          paragraphs: [
            "Une bonne conformité n'est pas un coût, c'est un signal de maturité. Les entreprises structurées peuvent démontrer leur sérieux aux partenaires, investisseurs et clients.",
            "Notion facilite la mise à jour continue des preuves et des rapports, ce qui évite les rushs de dernière minute et améliore la crédibilité."
          ],
          bullets: [
            'Moins de stress lors des audits',
            'Reporting plus clair pour les parties prenantes',
            'Vision partagée des risques'
          ]
        },
        {
          title: 'Cas d’usage prioritaires',
          paragraphs: [
            "CSRD et ESG : structurer les indicateurs et les preuves. AI Act : documenter la gouvernance et les risques IA. Qualiopi : suivre les indicateurs, audits et preuves pédagogiques.",
            "Ces cas d'usage ont un point commun : ils exigent de la rigueur, de la traçabilité et une mise à jour continue. Notion couvre ces besoins sans complexité technique."
          ]
        }
      ],
      templateLinkIds: [
        'conformite-csrd',
        'conformite-ai-act-reglement-europeen-ia',
        'bilan-carbone-entreprise-beges-ghg-protocol',
        'qualiopi-systeme-qualite'
      ],
      articleLinkSlugs: [
        'ai-act-gouvernance-ia-b2b',
        'csrd-bilan-carbone-notion',
        'qualiopi-pilotage-formation-b2b'
      ]
    },
    templateIds: [
      'conformite-csrd',
      'conformite-ai-act-reglement-europeen-ia',
      'bilan-carbone-entreprise-beges-ghg-protocol',
      'qualiopi-systeme-qualite'
    ],
    articleSlugs: [
      'ai-act-gouvernance-ia-b2b',
      'csrd-bilan-carbone-notion',
      'qualiopi-pilotage-formation-b2b'
    ],
    relatedPillarPaths: ['/qualiopi-notion-guide-complet', '/organisme-de-formation-notion'],
    seo: {
      title: 'Notion Conformité | CSRD, AI Act, Qualiopi',
      description:
        'Page pilier conformité : pourquoi Notion, templates dédiés et articles de référence.',
      keywords: [
        'notion conformité',
        'csrd',
        'ai act',
        'qualiopi',
        'rse'
      ],
      openGraph: {
        title: 'Notion pour la conformité',
        description:
          'Gérez conformité, reporting et preuves dans un espace unique.',
        type: 'website',
        image: '/images/templates/csrd_compliance.jpg'
      }
    },
    category: 'thematique'
  },
  {
    id: 'qualiopi',
    slug: 'qualiopi',
    path: '/qualiopi-notion-guide-complet',
    title: 'Qualiopi sur Notion : le guide complet',
    subtitle:
      'Un vrai guide pour structurer les 32 indicateurs, les preuves et les audits autour d\'un template opérationnel.',
    description:
      'Un guide complet pour comprendre Qualiopi, centraliser les preuves et choisir le bon template Notion pour l\'audit initial, la surveillance et le pilotage qualité.',
    heroImage: '/images/templates/qualiopi.jpg',
    problem: {
      title: 'Le problème',
      description:
        'La certification Qualiopi exige des preuves documentées pour chaque indicateur, mais les organismes de formation peinent à centraliser et maintenir leur système qualité.',
      bullets: [
        '32 indicateurs dispersés entre fichiers et tableurs',
        'Preuves difficiles à retrouver lors des audits',
        'Mise à jour du système qualité chronophage'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion centralise les 32 indicateurs, les preuves, les formations et les audits dans un espace unique, prêt pour les contrôles.',
      bullets: [
        'Indicateurs Qualiopi reliés aux preuves et documents',
        'Suivi des formations, formateurs et apprenants',
        'Système audit-ready et maintenable'
      ]
    },
    longForm: {
      intro: [
        "Qualiopi est la certification qualité de référence pour les prestataires de formation. Pour l'obtenir puis la conserver, il faut démontrer une conformité vivante sur 32 indicateurs répartis en 7 critères, avec des preuves accessibles, à jour et cohérentes.",
        "Dans la pratique, beaucoup d'organismes de formation connaissent le référentiel, mais peinent à transformer cette connaissance en système quotidien. Les informations vivent dans plusieurs dossiers, les documents sont mis à jour de façon irrégulière, et la préparation des audits repose encore trop souvent sur des efforts de dernière minute.",
        "Cette page a été pensée comme un vrai guide : elle vous aide à comprendre ce que vous devez structurer, quelles preuves centraliser, comment anticiper les audits et quel template utiliser selon votre niveau de maturité. L'objectif n'est pas de rester au niveau théorique, mais de vous orienter vers un système réellement actionnable.",
        "Que vous prépariez une première certification, un audit de surveillance ou une remise à plat de votre organisation qualité, l'enjeu est le même : rendre Qualiopi plus simple à maintenir, plus lisible pour l'équipe et plus utile pour le pilotage global de votre organisme."
      ],
      sections: [
        {
          title: 'À quoi sert vraiment un guide Qualiopi',
          paragraphs: [
            "Un bon guide Qualiopi ne sert pas seulement à lister les indicateurs. Il doit vous aider à transformer une obligation réglementaire en système clair : qui fait quoi, quelles preuves sont attendues, quels documents doivent vivre dans le temps et comment les retrouver facilement le jour de l'audit.",
            "C'est précisément l'intérêt d'une approche par template Notion : relier les indicateurs, les preuves, les formations, les audits et les actions correctives dans une même structure, au lieu de laisser ces éléments vivre dans des dossiers séparés.",
            "Autrement dit, un guide utile doit réduire l'écart entre la lecture du référentiel et sa mise en oeuvre opérationnelle. Il doit vous aider à décider ce que vous devez documenter, ce qui peut être standardisé, ce qui doit être mis à jour régulièrement et comment rendre tout cela visible pour l'équipe."
          ],
          bullets: [
            'Vision claire du système qualité',
            'Passage plus fluide de la théorie à l\'opérationnel',
            'Lien direct entre guide et template recommandé'
          ]
        },
        {
          title: 'Comprendre les 32 indicateurs Qualiopi',
          paragraphs: [
            "Les 32 indicateurs couvrent les conditions d'information du public, l'adaptation des parcours, l'accompagnement des apprenants, l'évaluation des acquis, les compétences des formateurs et la prise en compte des spécificités des publics. Chaque indicateur exige des preuves documentées.",
            "Notion permet de créer une base par indicateur : exigences officielles, preuves attendues, documents sources, responsables et fréquence de mise à jour. Vous gardez une vision claire de l'état d'avancement et des actions à mener.",
            "Cette lecture structurée des 32 indicateurs est essentielle, car elle évite deux pièges fréquents : traiter Qualiopi comme un simple dossier documentaire, ou au contraire se perdre dans une masse de documents sans lien clair avec les exigences du référentiel."
          ],
          bullets: [
            'Vue consolidée des 32 indicateurs',
            'Preuves reliées à chaque exigence',
            'Suivi des actions correctives'
          ]
        },
        {
          title: 'Les 7 critères Qualiopi en pratique',
          paragraphs: [
            "Les 7 critères forment l'ossature du référentiel : information du public, conception des prestations, accompagnement, moyens, qualification des intervenants, investissement dans l'environnement professionnel et recueil des appréciations. Pris séparément, ils peuvent sembler très administratifs.",
            "En pratique, ils prennent sens quand vous les rattachez à vos opérations réelles. C'est pourquoi un template utile doit relier référentiel, documents, sessions, formateurs, retours apprenants et actions d'amélioration.",
            "Cette approche est particulièrement utile pour les organismes qui veulent éviter une lecture trop abstraite de Qualiopi. Chaque critère doit pouvoir se traduire par des routines simples : mise à jour de documents, suivi d'indicateurs, collecte de feedbacks, preuve de compétences ou traitement des écarts."
          ],
          bullets: [
            'Critères reliés aux processus réels',
            'Moins de lecture abstraite du référentiel',
            'Vision exploitable pour l\'équipe qualité'
          ]
        },
        {
          title: 'Structurer les preuves et les documents qualité',
          paragraphs: [
            "Un audit Qualiopi repose sur des preuves tangibles : CGV, conventions de formation, règlements intérieurs, questionnaires de satisfaction, comptes rendus, fiches de poste. Ces documents doivent être à jour, accessibles et reliés aux indicateurs.",
            "Notion centralise tous ces documents dans des bases structurées. Chaque preuve est liée à l'indicateur qu'elle démontre, ce qui facilite la préparation du dossier d'audit et la réponse aux demandes des auditeurs.",
            "Au-delà du simple stockage, la vraie valeur vient de la traçabilité : savoir qui a mis à jour le document, à quelle date, pour quel indicateur et dans quel contexte. C'est cette logique qui rend un système qualité plus fiable dans la durée."
          ]
        },
        {
          title: 'Comment mettre en place un système Qualiopi dans Notion',
          paragraphs: [
            "La meilleure façon d'avancer n'est pas de vouloir tout modéliser en un jour. Commencez par une architecture simple : une base des indicateurs, une base des preuves, une base des audits et une base des actions correctives. Ensuite, reliez progressivement vos formations, vos formateurs et vos documents à ce socle.",
            "Cette mise en place progressive permet d'obtenir rapidement un système utile sans bloquer l'équipe. Vous pouvez d'abord viser la clarté, puis enrichir votre structure avec des vues par critère, des filtres par statut, des responsables et des échéances de mise à jour.",
            "Dans cette logique, le template sert de raccourci. Il évite de repartir de zéro et vous donne immédiatement une structure cohérente, que vous pouvez ensuite adapter à la réalité de votre organisme."
          ],
          bullets: [
            'Démarrage progressif sans usine à gaz',
            'Socle commun pour documents, audits et preuves',
            'Template personnalisable selon votre organisation'
          ]
        },
        {
          title: 'Audit initial, surveillance et renouvellement',
          paragraphs: [
            "Les besoins ne sont pas exactement les mêmes selon que vous préparez un audit initial, un audit de surveillance ou un renouvellement. L'audit initial demande surtout de démontrer que le système existe. La surveillance vérifie davantage que ce système est vivant, mis à jour et réellement utilisé.",
            "Un bon template Qualiopi doit donc servir avant, pendant et après l'audit. Il doit vous aider à préparer les preuves, tracer les écarts, suivre les actions correctives et garder l'historique des contrôles.",
            "Cette continuité est souvent sous-estimée. Pourtant, un système qualité robuste n'est pas seulement utile la semaine de l'audit. Il sert à documenter l'amélioration continue toute l'année, ce qui réduit le stress et rend les audits beaucoup plus prévisibles."
          ],
          bullets: [
            'Préparation plus fluide des audits',
            'Historique clair des contrôles',
            'Suivi des actions correctives dans le temps'
          ]
        },
        {
          title: 'Piloter les formations et les audits',
          paragraphs: [
            "Au-delà de la conformité, un bon système Qualiopi sert au pilotage : suivi des sessions, évaluations des apprenants, satisfaction, actions correctives. Ces données alimentent l'amélioration continue et prouvent la maturité de l'organisme.",
            "Notion relie formations, formateurs, apprenants et audits dans un tableau de bord unique. Vous anticipez les contrôles et démontrez une démarche qualité vivante, pas seulement documentaire.",
            "C'est ce qui fait la différence entre un guide purement informatif et un guide utile : vous ne lisez pas seulement ce qu'il faut faire, vous voyez aussi comment transformer Qualiopi en système de pilotage plus stable, plus lisible et plus efficace pour l'ensemble de l'organisation."
          ],
          bullets: [
            'Tableau de bord formations et sessions',
            'Historique des audits et non-conformités',
            'Réduction du temps de préparation des contrôles'
          ]
        }
      ],
      templateLinkIds: [
        'qualiopi-systeme-qualite',
        'audit-qualiopi-checklist-express-15-points',
        'dashboard-formateur'
      ],
      articleLinkSlugs: [
        'qualiopi-pilotage-formation-b2b',
        'audit-qualiopi-preuves-notion',
        'organisme-de-formation-gestion-apprenants-notion'
      ]
    },
    templateIds: [
      'qualiopi-systeme-qualite',
      'audit-qualiopi-checklist-express-15-points',
      'dashboard-formateur'
    ],
    articleSlugs: [
      'qualiopi-pilotage-formation-b2b',
      'audit-qualiopi-preuves-notion',
      'organisme-de-formation-gestion-apprenants-notion',
      'documents-obligatoires-organisme-de-formation'
    ],
    relatedPillarPaths: [
      '/organisme-de-formation-notion',
      '/notion-formateurs',
      '/notion-conformite'
    ],
    seo: {
      title: 'Qualiopi sur Notion : le guide complet | Certification et système qualité',
      description:
        'Guide complet Qualiopi : structurer les 32 indicateurs, les preuves et les audits dans Notion. Templates et articles pour organismes de formation.',
      keywords: [
        'qualiopi notion',
        'qualiopi guide',
        'certification qualiopi',
        'système qualité formation',
        'organisme de formation qualiopi',
        'notion qualiopi'
      ],
      openGraph: {
        title: 'Qualiopi sur Notion : le guide complet',
        description:
          'Structurer la certification Qualiopi avec Notion : 32 indicateurs, preuves et pilotage des formations.',
        type: 'website',
        image: '/images/templates/qualiopi.jpg'
      }
    },
    customCta: {
      title: 'Le template Qualiopi recommandé pour passer à l\'action',
      description:
        'Si vous voulez une structure directement exploitable pour vos indicateurs, vos preuves et vos audits, commencez par le système qualité complet.',
      buttonText: 'Voir le template Qualiopi',
      contactSubject: 'Qualiopi'
    },
    heroCta: {
      primary: { label: 'Voir le template Qualiopi', to: '/qualiopi-systeme-qualite' },
      secondary: { label: 'Préparer mes audits', to: '/blog/audit-qualiopi-preuves-notion' }
    },
    category: 'thematique'
  },
  {
    id: 'organismes-formation',
    slug: 'organisme-de-formation-notion',
    path: '/organisme-de-formation-notion',
    title: 'Notion pour les organismes de formation',
    subtitle:
      'Pilotez catalogue, sessions, apprenants, documents et conformité dans un seul système.',
    description:
      'Un hub SEO complet pour les organismes de formation, CFA et centres de formation qui veulent structurer leur gestion avec Notion.',
    heroImage: '/images/templates/dashboard_formateur.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Un organisme de formation jongle entre catalogue, sessions, apprenants, évaluations, documents administratifs et obligations Qualiopi.',
      bullets: [
        'Suivi des sessions dispersé entre mails, Drive et tableurs',
        'Données apprenants et évaluations difficiles à consolider',
        'Conformité Qualiopi traitée à part du pilotage quotidien'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion permet de relier le pilotage pédagogique, l\'administratif et la conformité dans un espace unique et maintenable.',
      bullets: [
        'Catalogue, sessions et participants reliés entre eux',
        'Documents, preuves et actions correctives centralisés',
        'Vues simples pour l\'équipe pédagogique comme pour le responsable qualité'
      ]
    },
    longForm: {
      intro: [
        "Les organismes de formation ont rarement un seul problème. Ils doivent en même temps vendre et structurer leur offre, planifier les sessions, suivre les apprenants, conserver les preuves pédagogiques et préparer les audits Qualiopi. Quand ces sujets vivent dans des outils séparés, la charge administrative explose.",
        "Cette page hub a un rôle clair : vous aider à comprendre comment Notion peut servir de socle opérationnel pour un organisme de formation. Elle oriente vers les bons templates et les bons articles selon votre intention : pilotage quotidien, suivi pédagogique, conformité ou préparation d'audit."
      ],
      sections: [
        {
          title: 'Structurer le catalogue et les sessions',
          paragraphs: [
            "Un organisme de formation a besoin d'un référentiel stable : programmes, objectifs, formats, durées, publics, prérequis, tarifs, ressources et intervenants. Quand ce catalogue est mal structuré, les équipes ressaisissent les mêmes informations dans plusieurs documents.",
            "Notion permet de créer une base catalogue reliée aux sessions réelles. Chaque session récupère ses informations clés, ses supports, ses participants et ses évaluations. On réduit la duplication et on gagne en cohérence commerciale comme pédagogique."
          ],
          bullets: [
            'Catalogue formation centralisé',
            'Sessions reliées aux programmes',
            'Moins de ressaisie entre vente, admin et pédagogie'
          ]
        },
        {
          title: 'Suivre les apprenants et la qualité pédagogique',
          paragraphs: [
            "Le suivi des apprenants ne se limite pas à une liste d'inscriptions. Il faut suivre l'assiduité, la progression, les évaluations, les retours de satisfaction et parfois les plans d'action. Sans structure, ces données restent fragmentées et inutilisables.",
            "Avec Notion, chaque apprenant peut être relié à ses sessions, ses résultats, ses documents et ses retours. Vous obtenez un vrai tableau de bord pédagogique qui sert au quotidien et nourrit votre démarche d'amélioration continue."
          ],
          bullets: [
            'Historique par apprenant et par session',
            'Évaluations et satisfaction consolidées',
            'Vision utile pour le pilotage et pour Qualiopi'
          ]
        },
        {
          title: 'Relier pilotage quotidien et conformité Qualiopi',
          paragraphs: [
            "Beaucoup d'organismes traitent Qualiopi comme un sujet séparé du reste. C'est une erreur coûteuse : les preuves demandées pendant les audits proviennent en réalité de la vie quotidienne de l'organisme.",
            "Un bon système Notion relie les sessions, les formateurs, les documents, les réclamations, les audits et les actions correctives. Vous évitez de reconstruire un dossier en urgence avant chaque contrôle."
          ],
          bullets: [
            'Preuves reliées aux processus réels',
            'Audit initial et surveillance plus fluides',
            'Démarche qualité vraiment opérationnelle'
          ]
        }
      ],
      templateLinkIds: [
        'dashboard-formateur',
        'qualiopi-systeme-qualite',
        'audit-qualiopi-checklist-express-15-points'
      ],
      articleLinkSlugs: [
        'logiciel-organisme-de-formation-comparatif-notion',
        'organisme-de-formation-gestion-apprenants-notion',
        'catalogue-formation-notion-modele',
        'audit-qualiopi-preuves-notion'
      ]
    },
    templateIds: [
      'dashboard-formateur',
      'qualiopi-systeme-qualite',
      'audit-qualiopi-checklist-express-15-points'
    ],
    articleSlugs: [
      'logiciel-organisme-de-formation-comparatif-notion',
      'organisme-de-formation-gestion-apprenants-notion',
      'catalogue-formation-notion-modele',
      'audit-qualiopi-preuves-notion',
      'qualiopi-pilotage-formation-b2b'
    ],
    seo: {
      title: 'Organisme de formation : piloter Notion, Qualiopi et vos sessions',
      description:
        'Guide complet pour les organismes de formation : catalogue, sessions, apprenants, évaluations et Qualiopi dans Notion.',
      keywords: [
        'organisme de formation',
        'logiciel organisme de formation',
        'notion organisme de formation',
        'gestion organisme de formation',
        'qualiopi notion',
        'tableau de bord formation'
      ],
      openGraph: {
        title: 'Notion pour les organismes de formation',
        description:
          'Structurez catalogue, sessions, apprenants et conformité Qualiopi dans un seul système.',
        type: 'website',
        image: '/images/templates/dashboard_formateur.jpg'
      }
    },
    customCta: {
      title: 'Besoin d\'un système clair pour votre organisme ?',
      description:
        'Choisissez un template orienté pilotage quotidien ou un système Qualiopi plus complet selon votre niveau de maturité.',
      buttonText: 'Parler de mon organisme',
      contactSubject: 'Organisme de formation'
    },
    heroCta: {
      primary: { label: 'Voir le Dashboard Formateur', to: '/dashboard-formateur' },
      secondary: { label: 'Lire le guide Qualiopi', to: '/qualiopi-notion-guide-complet' }
    },
    relatedPillarPaths: ['/notion-formateurs', '/qualiopi-notion-guide-complet', '/notion-conformite'],
    category: 'thematique'
  },
  {
    id: 'collection',
    slug: 'collection',
    path: '/notion-collection',
    title: 'Notion pour les collections',
    subtitle: 'Suivez vos cartes, valeurs et wishlist au même endroit.',
    description:
      'Un hub durable pour les collectionneurs : suivi, valorisation et organisation.',
    heroImage: '/images/templates/cartes_pokemon_collection_tracker.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Une collection grandit vite : sans système, vous perdez le contrôle sur les cartes, la valeur et les achats.',
      bullets: [
        'Inventaire incomplet ou obsolète',
        'Valeurs de cartes non suivies',
        'Wishlist et budget difficiles à piloter'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion devient votre base de données personnelle pour collecter, analyser et suivre chaque carte.',
      bullets: [
        'Catalogues détaillés et filtrables',
        'Suivi financier automatique',
        'Wishlist priorisée par objectifs'
      ]
    },
    longForm: {
      intro: [
        "Une collection est un actif : elle prend de la valeur avec le temps, mais seulement si elle est suivie correctement. Sans méthode claire, il devient difficile de savoir ce que l'on possède, ce que l'on recherche et combien cela représente.",
        "Cette page pilier rassemble les meilleures ressources pour les collectionneurs. Elle explique comment Notion permet de structurer votre collection et renvoie vers les templates et articles les plus utiles."
      ],
      sections: [
        {
          title: 'Créer un inventaire fiable',
          paragraphs: [
            "L'inventaire est la base : cartes, sets, raretés, état, valeur. Avec Notion, chaque carte devient une entrée liée à un set, un prix et un historique.",
            "Vous gagnez une vision claire de votre collection, avec des filtres pour trier par série, génération ou état."
          ]
        },
        {
          title: 'Suivre la valeur et les achats',
          paragraphs: [
            "Le suivi financier est souvent le point faible des collectionneurs. Notion permet de suivre prix d'achat, valeur estimée et plus-value en un seul tableau.",
            "Cela aide à mieux arbitrer : acheter, revendre ou conserver. Vous gardez un historique fiable pour éviter les achats impulsifs."
          ],
          bullets: [
            'Vision globale de la valeur',
            'Budget d’achat maîtrisé',
            'Wishlist priorisée'
          ]
        },
        {
          title: 'Créer une stratégie de collection',
          paragraphs: [
            "Une collection bien gérée n'est pas seulement un plaisir, c'est une stratégie. Notion permet d'aligner votre collection avec vos objectifs : compléter un set, investir sur des cartes clés ou simplement conserver une trace.",
            "Avec des bases claires, vous transformez votre passion en système durable."
          ]
        }
      ],
      templateLinkIds: [
        'cartes-pokemon-collection-tracker',
        'magic-the-gathering-gestionnaire-complet',
        'cartes-yu-gi-oh-gestionnaire-complet'
      ],
      articleLinkSlugs: ['collection-cartes-pokemon-notion']
    },
    templateIds: [
      'cartes-pokemon-collection-tracker',
      'magic-the-gathering-gestionnaire-complet',
      'cartes-yu-gi-oh-gestionnaire-complet'
    ],
    articleSlugs: ['collection-cartes-pokemon-notion'],
    seo: {
      title: 'Notion Collection | Organiser et valoriser vos cartes',
      description:
        'Page pilier collection : suivi de cartes TCG, templates dédiés et article de référence.',
      keywords: [
        'notion collection',
        'cartes pokemon',
        'magic the gathering',
        'yu-gi-oh',
        'tracker'
      ],
      openGraph: {
        title: 'Notion pour les collections',
        description:
          'Suivez vos collections TCG avec des bases Notion prêtes à l’emploi.',
        type: 'website',
        image: '/images/templates/cartes_pokemon_collection_tracker.jpg'
      }
    },
    category: 'thematique'
  },
  {
    id: 'ecriture',
    slug: 'ecriture',
    path: '/notion-ecriture',
    title: 'Notion pour l’écriture',
    subtitle: 'Organisez idées, univers et progression de vos textes.',
    description:
      'Un hub SEO pour les auteurs et créateurs qui veulent structurer leur écriture.',
    heroImage: '/images/templates/creation_d_univers_et_planificateur_de_roman.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les idées se dispersent et la cohérence narrative devient difficile à tenir sans système clair.',
      bullets: [
        'Notes et recherches éparpillées',
        'Univers et personnages mal reliés',
        'Suivi d’avancement peu fiable'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion relie vos idées, vos scènes et votre progression dans un espace unique.',
      bullets: [
        'Worldbuilding et fiches personnages',
        'Planification des chapitres',
        'Suivi d’écriture et objectifs'
      ]
    },
    longForm: {
      intro: [
        "L'écriture est un travail de longue haleine. Sans cadre, les idées se perdent, la cohérence narrative s'affaiblit et la motivation décroît. Un bon système permet de rester aligné sur son univers et ses objectifs.",
        "Cette page pilier est un guide durable pour les auteurs. Elle explique comment Notion peut structurer un projet d'écriture et propose des ressources concrètes."
      ],
      sections: [
        {
          title: 'Structurer l’univers',
          paragraphs: [
            "Personnages, lieux, chronologie, magie ou technologie : chaque élément de votre univers doit rester cohérent. Notion permet de relier ces informations pour éviter les contradictions.",
            "Vous gardez une trace claire de vos choix narratifs, ce qui facilite les révisions et la rédaction."
          ]
        },
        {
          title: 'Planifier le récit',
          paragraphs: [
            "Un plan solide accélère l'écriture. Notion offre un espace pour découper un roman en chapitres, scènes et arcs narratifs.",
            "Vous visualisez la progression, vous suivez l'avancement et vous évitez les blocages liés à un manque de structure."
          ],
          bullets: [
            'Vision globale de l’intrigue',
            'Suivi des scènes et des objectifs',
            'Cadence d’écriture maîtrisée'
          ]
        },
        {
          title: 'Créer un second cerveau créatif',
          paragraphs: [
            "L'écrivain moderne jongle avec des recherches, des notes et des idées nouvelles. Notion devient un second cerveau où tout est rangé, retrouvable et connecté.",
            "C'est un moyen simple de conserver votre inspiration et d'en faire une force d'exécution."
          ]
        }
      ],
      templateLinkIds: [
        'creation-d-univers-et-planificateur-de-roman',
        'systeme-second-cerveau',
        'content-creator-hub'
      ],
      articleLinkSlugs: [
        'creation-roman-heroic-fantasy-notion',
        'content-creator-second-cerveau'
      ]
    },
    templateIds: [
      'creation-d-univers-et-planificateur-de-roman',
      'systeme-second-cerveau',
      'content-creator-hub'
    ],
    articleSlugs: [
      'creation-roman-heroic-fantasy-notion',
      'content-creator-second-cerveau'
    ],
    seo: {
      title: 'Notion Écriture | Planifier romans et contenus',
      description:
        'Page pilier écriture : templates d’écriture, second cerveau et articles associés.',
      keywords: [
        'notion ecriture',
        'roman',
        'worldbuilding',
        'second cerveau',
        'content creator'
      ],
      openGraph: {
        title: 'Notion pour l’écriture',
        description:
          'Structurez votre roman et vos contenus avec Notion.',
        type: 'website',
        image: '/images/templates/creation_d_univers_et_planificateur_de_roman.jpg'
      }
    },
    category: 'thematique'
  },
  {
    id: 'formateurs',
    slug: 'formateurs',
    path: '/notion-formateurs',
    title: 'Notion pour les formateurs',
    subtitle: 'Pilotez vos formations, vos contenus et vos apprenants sans alourdir votre administratif.',
    description:
      'Un hub durable pour les formateurs indépendants, consultants pédagogiques et petites structures de formation.',
    heroImage: '/images/templates/qualiopi.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Entre préparation des sessions, suivi des apprenants et production des livrables pédagogiques, la charge mentale monte vite.',
      bullets: [
        'Supports et ressources pédagogiques dispersés',
        'Suivi des participants peu lisible au quotidien',
        'Organisation fragile dès que l\'activité grandit'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion réunit planning, contenus, sessions et suivi pédagogique dans un tableau de bord simple à faire vivre.',
      bullets: [
        'Pilotage des sessions et modules',
        'Suivi pédagogique des apprenants',
        'Base solide avant un futur passage à Qualiopi'
      ]
    },
    longForm: {
      intro: [
        "Les formateurs indépendants et consultants pédagogiques ont besoin d'un système léger mais rigoureux. Ils doivent préparer leurs sessions, organiser leurs contenus, suivre leurs participants et garder une trace exploitable des retours et résultats.",
        "Cette page pilier se concentre sur l'usage métier et pédagogique de Notion. Si vous gérez un organisme de formation plus structuré ou une démarche Qualiopi complète, le hub dédié organismes de formation vous orientera vers une architecture plus large."
      ],
      sections: [
        {
          title: 'Centraliser la gestion de vos sessions',
          paragraphs: [
            "Notion permet de réunir programmes, sessions, objectifs, supports et to-do pédagogiques dans un même espace. Vous savez quoi animer, quand, avec quels supports et pour quel public.",
            "Cette centralisation réduit la perte d'information entre la conception de l'offre, la préparation des animations et le suivi post-session."
          ]
        },
        {
          title: 'Suivre les participants avec plus de rigueur',
          paragraphs: [
            "Même sans grosse structure, un formateur a besoin de suivre les inscriptions, la présence, les évaluations, les retours et parfois les demandes spécifiques des apprenants. Un système trop léger devient vite insuffisant quand l'activité augmente.",
            "Notion offre des bases simples à maintenir, avec des vues par session, par participant ou par programme. Vous gardez la main sans vous retrouver dans un logiciel lourd."
          ],
          bullets: [
            'Vue claire par session et par apprenant',
            'Évaluations et feedbacks consolidés',
            'Meilleure continuité pédagogique'
          ]
        },
        {
          title: 'Passer du freelance à une organisation plus structurée',
          paragraphs: [
            "Un bon système Notion vous permet de professionnaliser votre activité sans ajouter de friction. Vous pouvez garder une structure simple au départ, puis enrichir vos bases si vous développez une offre plus large ou si vous devez préparer Qualiopi.",
            "C'est le bon angle pour les formateurs qui veulent mieux piloter aujourd'hui, tout en gardant une trajectoire vers un système plus complet demain."
          ]
        }
      ],
      templateLinkIds: [
        'dashboard-formateur',
        'qualiopi-systeme-qualite',
        'guide-ia-llm-coachs-formateurs'
      ],
      articleLinkSlugs: [
        'organisme-de-formation-gestion-apprenants-notion',
        'catalogue-formation-notion-modele',
        'comment-se-former-notion-guide-formateurs'
      ]
    },
    templateIds: [
      'dashboard-formateur',
      'qualiopi-systeme-qualite',
      'guide-ia-llm-coachs-formateurs'
    ],
    articleSlugs: [
      'organisme-de-formation-gestion-apprenants-notion',
      'catalogue-formation-notion-modele',
      'comment-se-former-notion-guide-formateurs',
      'logiciel-organisme-de-formation-comparatif-notion'
    ],
    relatedPillarPaths: ['/organisme-de-formation-notion', '/qualiopi-notion-guide-complet'],
    seo: {
      title: 'Notion Formateurs | Organisation, sessions et suivi pédagogique',
      description:
        'Page pilier formateurs : organisation des sessions, suivi des apprenants et ressources Notion pour formateurs.',
      keywords: [
        'notion formateurs',
        'formateur indépendant',
        'gestion formation',
        'suivi apprenants',
        'pilotage pédagogique',
        'notion formation'
      ],
      openGraph: {
        title: 'Notion pour les formateurs',
        description:
          'Organisez votre activité de formation avec Notion.',
        type: 'website',
        image: '/images/templates/qualiopi.jpg'
      }
    },
    category: 'thematique'
  },
  // ──────────────────────────────────────────────────────────
  // Pages métiers — SEO sur mesure
  // ──────────────────────────────────────────────────────────
  {
    id: 'experts-comptables',
    slug: 'experts-comptables',
    path: '/notion-experts-comptables',
    title: 'Notion pour les experts-comptables',
    subtitle:
      'Structurez votre cabinet, vos dossiers clients et vos échéances fiscales dans un espace unique.',
    description:
      'Les cabinets comptables jonglent entre déclarations, bilans, pièces justificatives et deadlines. Notion centralise tout pour gagner en fiabilité et en sérénité.',
    heroImage: '/images/templates/family_office_gestion_complete_patrimoine.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les cabinets d\'experts-comptables gèrent des dizaines de dossiers simultanés avec des outils fragmentés.',
      bullets: [
        'Échéances fiscales suivies sur tableur, faciles à rater',
        'Pièces justificatives dispersées entre emails et dossiers partagés',
        'Aucune vue consolidée sur la charge de travail du cabinet'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion transforme votre cabinet en système organisé : chaque client, chaque échéance, chaque document à sa place.',
      bullets: [
        'Fiches clients reliées aux échéances et documents',
        'Tableaux de bord de suivi par collaborateur',
        'Bibliothèque de procédures internes partagée'
      ]
    },
    longForm: {
      intro: [
        "Un cabinet comptable performant ne repose pas uniquement sur l'expertise technique. Il dépend aussi de la qualité de son organisation interne : suivi des mandats, respect des échéances, accès rapide aux pièces, coordination entre collaborateurs.",
        "Notion offre un cadre flexible pour structurer ces flux sans rigidité logicielle. Cette page explique comment un template Notion sur mesure peut transformer la gestion de votre cabinet."
      ],
      sections: [
        {
          title: 'Piloter les dossiers clients',
          paragraphs: [
            "Chaque client a ses spécificités : régime fiscal, périodicité des déclarations, interlocuteurs. Un espace Notion dédié relie le dossier client à ses échéances, ses documents et ses échanges.",
            "Vous visualisez instantanément l'état d'avancement de chaque dossier et pouvez anticiper les pics d'activité (clôtures annuelles, TVA trimestrielle, liasses fiscales)."
          ],
          bullets: [
            'Vue Kanban par statut de dossier',
            'Alertes sur les échéances à venir',
            'Historique des échanges client centralisé'
          ]
        },
        {
          title: 'Sécuriser la conformité et la traçabilité',
          paragraphs: [
            "Les obligations réglementaires imposent une traçabilité stricte. Notion permet de documenter chaque étape : qui a validé quoi, quand, avec quelles pièces.",
            "En cas de contrôle fiscal ou d'audit, toutes les informations sont accessibles en quelques clics, sans fouiller dans des dossiers physiques ou des arborescences de fichiers."
          ]
        },
        {
          title: 'Standardiser les procédures du cabinet',
          paragraphs: [
            "Les cabinets qui grandissent ont besoin de processus reproductibles. Notion sert de base de connaissances interne : procédures de clôture, checklist d'intégration d'un nouveau client, modèles de lettres de mission.",
            "Chaque collaborateur sait quoi faire et dans quel ordre, ce qui réduit les erreurs et accélère la formation des nouveaux arrivants."
          ]
        }
      ],
      templateLinkIds: [
        'family-office-gestion-patrimoine',
        'crm-freelance-gratuit-gestion-autoentrepreneur'
      ],
      articleLinkSlugs: []
    },
    templateIds: [
      'family-office-gestion-patrimoine',
      'crm-freelance-gratuit-gestion-autoentrepreneur',
      'conformite-csrd'
    ],
    articleSlugs: [],
    seo: {
      title: 'Template Notion Expert-Comptable | Cabinet comptable sur mesure',
      description:
        'Organisez votre cabinet comptable avec Notion : suivi clients, échéances fiscales, documents et procédures. Template sur mesure disponible.',
      keywords: [
        'notion expert comptable',
        'template notion cabinet comptable',
        'gestion cabinet comptable notion',
        'suivi dossiers clients comptabilité',
        'organisation expert comptable'
      ],
      openGraph: {
        title: 'Notion pour les experts-comptables',
        description:
          'Structurez votre cabinet comptable avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/family_office_gestion_complete_patrimoine.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion conçu pour votre cabinet comptable',
      description: 'Chaque cabinet a ses spécificités. Nous créons un espace Notion sur mesure adapté à votre organisation, vos clients et vos échéances.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Cabinet d\'expert-comptable'
    },
    category: 'metier'
  },
  {
    id: 'avocats',
    slug: 'avocats',
    path: '/notion-avocats',
    title: 'Notion pour les avocats',
    subtitle:
      'Gérez vos dossiers, vos échéances judiciaires et votre facturation dans un espace structuré.',
    description:
      'Les cabinets d\'avocats traitent des dossiers complexes avec des délais stricts. Notion centralise la gestion pour ne rien laisser passer.',
    heroImage: '/images/templates/deal_room_ma_levee_de_fonds.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les avocats passent un temps considérable à chercher des pièces, suivre des procédures et coordonner les intervenants.',
      bullets: [
        'Dossiers répartis entre emails, Cloud et dossiers papier',
        'Échéances procédurales critiques difficiles à suivre',
        'Temps passé non facturé par manque de suivi'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion devient le dossier unique de chaque affaire : pièces, échéances, notes d\'audience et suivi du temps.',
      bullets: [
        'Dossier client centralisé avec toutes les pièces',
        'Calendrier des audiences et délais de procédure',
        'Suivi du temps passé pour la facturation'
      ]
    },
    longForm: {
      intro: [
        "La pratique du droit exige rigueur et organisation. Un avocat gère simultanément des dizaines de dossiers, chacun avec ses échéances, ses pièces et ses parties prenantes. Sans système structuré, le risque d'erreur ou d'oubli est réel.",
        "Notion permet de créer un environnement de travail adapté à la pratique juridique. Cette page explique comment un template sur mesure peut transformer votre quotidien d'avocat."
      ],
      sections: [
        {
          title: 'Centraliser chaque dossier',
          paragraphs: [
            "Un dossier juridique rassemble des conclusions, des pièces adverses, des notes d'audience, de la correspondance et des délais. Dans Notion, chaque dossier devient une page reliée à toutes ces informations.",
            "Vous accédez à l'historique complet d'une affaire en un clic, que vous soyez au cabinet ou en déplacement."
          ],
          bullets: [
            'Arborescence claire par affaire',
            'Pièces et conclusions classées chronologiquement',
            'Notes d\'audience horodatées'
          ]
        },
        {
          title: 'Ne manquer aucune échéance',
          paragraphs: [
            "Les délais de procédure sont impératifs. Un retard peut entraîner l'irrecevabilité d'une demande. Notion permet de créer un calendrier procédural relié à chaque dossier.",
            "Les rappels et les vues filtrées par urgence garantissent que rien ne passe entre les mailles du filet."
          ]
        },
        {
          title: 'Optimiser la facturation',
          paragraphs: [
            "Le suivi du temps passé est souvent négligé faute d'outil simple. Avec Notion, chaque intervention est enregistrée directement dans le dossier : durée, nature de l'acte, collaborateur.",
            "Cela simplifie la facturation et permet d'analyser la rentabilité par dossier ou par type d'affaire."
          ]
        }
      ],
      templateLinkIds: [
        'deal-room-ma-levee-de-fonds',
        'mini-audit-rgpd-starter-kit-gratuit'
      ],
      articleLinkSlugs: []
    },
    templateIds: [
      'deal-room-ma-levee-de-fonds',
      'mini-audit-rgpd-starter-kit-gratuit',
      'conformite-ai-act-reglement-europeen-ia'
    ],
    articleSlugs: [],
    seo: {
      title: 'Template Notion Avocat | Gestion cabinet juridique sur mesure',
      description:
        'Organisez votre cabinet d\'avocats avec Notion : dossiers clients, échéances judiciaires, facturation et suivi du temps. Template sur mesure.',
      keywords: [
        'notion avocat',
        'template notion cabinet avocat',
        'gestion dossiers juridiques notion',
        'suivi échéances judiciaires',
        'organisation cabinet avocat'
      ],
      openGraph: {
        title: 'Notion pour les avocats',
        description:
          'Structurez votre cabinet juridique avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/deal_room_ma_levee_de_fonds.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion taillé pour votre pratique juridique',
      description: 'Droit des affaires, pénal, social ou famille : nous concevons un espace Notion adapté à votre spécialité et à votre organisation.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Cabinet d\'avocats'
    },
    category: 'metier'
  },
  {
    id: 'coachs-sportifs',
    slug: 'coachs-sportifs',
    path: '/notion-coachs-sportifs',
    title: 'Notion pour les coachs sportifs',
    subtitle:
      'Planifiez vos séances, suivez vos clients et développez votre activité depuis un seul espace.',
    description:
      'Les coachs sportifs et personal trainers gèrent planning, suivi client et administratif. Notion unifie tout dans un dashboard clair.',
    heroImage: '/images/templates/crm_freelance_gratuit_gestion_autoentrepreneur.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Entre les séances, le suivi des progrès et la gestion administrative, les coachs perdent un temps précieux.',
      bullets: [
        'Planning géré sur papier ou via plusieurs apps',
        'Progrès des clients non documentés systématiquement',
        'Facturation et suivi administratif chronophages'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion centralise planning, fiches clients et programmes d\'entraînement dans un espace accessible partout.',
      bullets: [
        'Fiches clients avec objectifs et mesures',
        'Bibliothèque d\'exercices et programmes types',
        'Planning hebdomadaire et suivi des séances'
      ]
    },
    longForm: {
      intro: [
        "Un coach sportif qui réussit ne se contente pas d'être bon techniquement. Il doit aussi gérer son planning, suivre les progrès de chaque client, personnaliser ses programmes et développer son activité commerciale.",
        "Notion offre un cadre flexible pour structurer tout cela sans multiplier les outils. Découvrez comment un template sur mesure peut professionnaliser votre activité de coaching."
      ],
      sections: [
        {
          title: 'Suivre chaque client individuellement',
          paragraphs: [
            "Chaque client a des objectifs, des contraintes et un historique. Notion permet de créer une fiche client complète : mensurations, objectifs, blessures, historique des séances.",
            "Vous visualisez les progrès dans le temps et adaptez vos programmes en fonction des résultats réels."
          ],
          bullets: [
            'Fiche client avec photos et mensurations',
            'Historique des séances et exercices',
            'Suivi des objectifs et jalons'
          ]
        },
        {
          title: 'Créer et organiser vos programmes',
          paragraphs: [
            "Une bibliothèque d'exercices classés par groupe musculaire, niveau et équipement permet de construire des programmes rapidement. Chaque programme est relié aux clients qui le suivent.",
            "Vous gagnez du temps sur la préparation et proposez des parcours cohérents et progressifs."
          ]
        },
        {
          title: 'Gérer le business',
          paragraphs: [
            "Au-delà du coaching, il faut gérer la facturation, les abonnements, les prospects et la communication. Notion regroupe ces aspects business dans un espace dédié.",
            "Vous avez une vue claire sur vos revenus, vos clients actifs et votre pipeline de prospects."
          ]
        }
      ],
      templateLinkIds: [
        'crm-freelance-gratuit-gestion-autoentrepreneur'
      ],
      articleLinkSlugs: []
    },
    templateIds: [
      'crm-freelance-gratuit-gestion-autoentrepreneur'
    ],
    articleSlugs: [],
    seo: {
      title: 'Template Notion Coach Sportif | Suivi clients et planning',
      description:
        'Gérez votre activité de coach sportif avec Notion : fiches clients, programmes d\'entraînement, planning et facturation. Template sur mesure.',
      keywords: [
        'notion coach sportif',
        'template notion personal trainer',
        'suivi clients coaching sportif',
        'gestion coach sportif notion',
        'planning coach sportif'
      ],
      openGraph: {
        title: 'Notion pour les coachs sportifs',
        description:
          'Structurez votre activité de coaching avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/crm_freelance_gratuit_gestion_autoentrepreneur.jpg'
      }
    },
    customCta: {
      title: 'Un espace Notion pensé pour votre activité de coaching',
      description: 'Coaching individuel, salle de sport ou préparation physique : nous créons un template adapté à votre méthode et vos clients.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Coach sportif'
    },
    category: 'metier'
  },
  {
    id: 'startups',
    slug: 'startups',
    path: '/notion-startups',
    title: 'Notion pour les start-ups',
    subtitle:
      'Pilotez produit, levée de fonds et croissance depuis un QG Notion centralisé.',
    description:
      'Les start-ups avancent vite et changent souvent. Notion s\'adapte à cette agilité en centralisant roadmap, metrics et fundraising.',
    heroImage: '/images/templates/deal_room_ma_levee_de_fonds.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les start-ups multiplient les outils sans jamais avoir une vision unifiée de leur activité.',
      bullets: [
        'Roadmap produit sur un outil, CRM sur un autre, docs sur un troisième',
        'Informations critiques perdues entre Slack, email et Google Drive',
        'Pas de source unique de vérité pour les investisseurs et le board'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion devient le wiki, le CRM et le tableau de bord de votre start-up, le tout dans un seul espace.',
      bullets: [
        'Wiki interne et documentation produit',
        'Roadmap et sprint planning',
        'Suivi de la levée de fonds et data room'
      ]
    },
    longForm: {
      intro: [
        "Une start-up a besoin de vitesse et de clarté. Les premiers mois sont critiques : il faut itérer sur le produit, convaincre des investisseurs, recruter les bonnes personnes et garder tout le monde aligné. Sans système, le chaos s'installe rapidement.",
        "Notion est devenu l'outil de référence des start-ups pour une bonne raison : il s'adapte à chaque phase de croissance. Mais un espace Notion générique ne suffit pas. Un template sur mesure fait toute la différence."
      ],
      sections: [
        {
          title: 'Structurer la roadmap produit',
          paragraphs: [
            "La roadmap est le cœur de la start-up. Notion permet de la relier aux specs, aux feedbacks utilisateurs et aux sprints. Chaque feature a un contexte, un statut et un responsable.",
            "L'équipe produit, tech et business travaille depuis la même source de vérité, ce qui accélère les décisions et réduit les malentendus."
          ],
          bullets: [
            'Backlog priorisé et sprint board',
            'Specs reliées aux user stories',
            'Changelog et release notes'
          ]
        },
        {
          title: 'Piloter la levée de fonds',
          paragraphs: [
            "Une levée de fonds est un projet en soi : pitch deck, data room, pipeline d'investisseurs, term sheets. Notion permet de tout centraliser et de suivre chaque contact.",
            "Quand un VC pose une question, vous avez la réponse en 30 secondes. Cette réactivité fait la différence."
          ]
        },
        {
          title: 'Scaler l\'organisation',
          paragraphs: [
            "À mesure que l'équipe grandit, les processus doivent se formaliser : onboarding, OKRs, rituels d'équipe, documentation. Notion grandit avec vous sans imposer de rigidité.",
            "Chaque nouveau collaborateur trouve un espace structuré et peut être opérationnel rapidement."
          ]
        }
      ],
      templateLinkIds: [
        'deal-room-ma-levee-de-fonds',
        'crm-freelance-gratuit-gestion-autoentrepreneur'
      ],
      articleLinkSlugs: []
    },
    templateIds: [
      'deal-room-ma-levee-de-fonds',
      'crm-freelance-gratuit-gestion-autoentrepreneur',
      'conformite-ai-act-reglement-europeen-ia'
    ],
    articleSlugs: [],
    seo: {
      title: 'Template Notion Start-up | Roadmap, levée de fonds et OKRs',
      description:
        'Structurez votre start-up avec Notion : roadmap produit, data room, CRM investisseurs et wiki interne. Template sur mesure disponible.',
      keywords: [
        'notion startup',
        'template notion start-up',
        'notion levée de fonds',
        'roadmap notion startup',
        'organisation startup notion'
      ],
      openGraph: {
        title: 'Notion pour les start-ups',
        description:
          'Pilotez votre start-up avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/deal_room_ma_levee_de_fonds.jpg'
      }
    },
    customCta: {
      title: 'Un espace Notion qui grandit avec votre start-up',
      description: 'Pré-seed, Série A ou scale-up : nous construisons le QG Notion adapté à votre stade et vos enjeux.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Start-up'
    },
    category: 'metier'
  },
  {
    id: 'gestion-financiere',
    slug: 'gestion-financiere',
    path: '/notion-gestion-financiere',
    title: 'Notion pour la gestion financière',
    subtitle:
      'Centralisez budgets, trésorerie et reporting dans un tableau de bord financier clair.',
    description:
      'Les DAF et responsables financiers ont besoin de visibilité. Notion structure les données financières pour un pilotage précis.',
    heroImage: '/images/templates/family_office_gestion_complete_patrimoine.jpg',
    problem: {
      title: 'Le problème',
      description:
        'La gestion financière repose souvent sur des tableurs complexes, peu collaboratifs et sujets aux erreurs.',
      bullets: [
        'Tableurs Excel critiques sans contrôle de version',
        'Reporting manuel chronophage chaque mois',
        'Pas de vue consolidée budget vs réalisé'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion ne remplace pas votre ERP, mais il devient le cockpit de pilotage financier accessible à toute l\'équipe.',
      bullets: [
        'Dashboard budget vs réalisé par département',
        'Suivi de trésorerie et prévisions',
        'Documentation des processus financiers'
      ]
    },
    longForm: {
      intro: [
        "La fonction financière est le pilier de toute organisation. Budget, trésorerie, clôtures, reporting aux actionnaires : les responsables financiers doivent garantir précision et réactivité dans un environnement de plus en plus complexe.",
        "Notion permet de créer un espace de pilotage financier complémentaire aux outils comptables. Un template sur mesure structure vos flux et donne de la visibilité à toute la direction."
      ],
      sections: [
        {
          title: 'Piloter le budget',
          paragraphs: [
            "Un budget n'est utile que s'il est suivi. Notion permet de créer un tableau de bord qui compare budget prévisionnel et réalisé, par département et par poste.",
            "Les écarts sont visibles immédiatement, ce qui permet des arbitrages rapides et documentés."
          ],
          bullets: [
            'Budget par centre de coût',
            'Suivi mensuel des écarts',
            'Alertes sur les dépassements'
          ]
        },
        {
          title: 'Suivre la trésorerie',
          paragraphs: [
            "La trésorerie est le nerf de la guerre. Notion offre un espace pour suivre les flux entrants et sortants, les échéances de paiement et les prévisions à court terme.",
            "Vous anticipez les tensions de trésorerie avant qu'elles ne deviennent critiques."
          ]
        },
        {
          title: 'Structurer le reporting',
          paragraphs: [
            "Le reporting financier prend du temps quand les données sont dispersées. Notion centralise les KPIs, les commentaires et les analyses dans un format lisible par la direction et le board.",
            "Chaque clôture mensuelle devient plus fluide et les présentations aux actionnaires sont prêtes plus rapidement."
          ]
        }
      ],
      templateLinkIds: [
        'family-office-gestion-patrimoine',
        'deal-room-ma-levee-de-fonds'
      ],
      articleLinkSlugs: []
    },
    templateIds: [
      'family-office-gestion-patrimoine',
      'deal-room-ma-levee-de-fonds',
      'conformite-csrd'
    ],
    articleSlugs: [],
    seo: {
      title: 'Template Notion DAF | Gestion financière et reporting',
      description:
        'Pilotez votre gestion financière avec Notion : budgets, trésorerie, reporting et clôtures. Template sur mesure pour DAF et responsables financiers.',
      keywords: [
        'notion gestion financière',
        'template notion daf',
        'suivi budget notion',
        'reporting financier notion',
        'trésorerie notion'
      ],
      openGraph: {
        title: 'Notion pour la gestion financière',
        description:
          'Centralisez votre pilotage financier avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/family_office_gestion_complete_patrimoine.jpg'
      }
    },
    customCta: {
      title: 'Un cockpit financier Notion adapté à votre entreprise',
      description: 'PME, ETI ou start-up : nous concevons un espace de pilotage budgétaire et de reporting sur mesure.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Gestion financière / DAF'
    },
    category: 'metier'
  },
  {
    id: 'ressources-humaines',
    slug: 'ressources-humaines',
    path: '/notion-ressources-humaines',
    title: 'Notion pour les ressources humaines',
    subtitle:
      'Recrutement, onboarding, suivi des collaborateurs et QVCT dans un espace RH centralisé.',
    description:
      'Les DRH et responsables RH gèrent des processus complexes et sensibles. Notion structure chaque étape du parcours collaborateur.',
    heroImage: '/images/templates/qualite_vie_travail_rps_qvct.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les services RH manquent souvent d\'outils adaptés pour gérer la diversité de leurs missions.',
      bullets: [
        'Recrutement suivi par email et tableur',
        'Onboarding non standardisé, chaque manager improvise',
        'Pas de vision consolidée du climat social et des indicateurs RH'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion centralise les processus RH dans un espace collaboratif, du recrutement au suivi QVCT.',
      bullets: [
        'Pipeline de recrutement visuel',
        'Checklist d\'onboarding standardisée',
        'Tableau de bord indicateurs RH et QVCT'
      ]
    },
    longForm: {
      intro: [
        "Les ressources humaines sont au cœur de la performance d'une organisation. Recrutement, intégration, formation, gestion des talents, qualité de vie au travail : les missions sont nombreuses et souvent gérées avec des outils inadaptés.",
        "Notion permet de créer un système RH complet, modulaire et évolutif. Un template sur mesure s'adapte à la taille de votre structure et à vos priorités RH."
      ],
      sections: [
        {
          title: 'Recruter efficacement',
          paragraphs: [
            "Un bon recrutement commence par un processus clair. Notion permet de suivre chaque candidature du sourcing à l'offre, avec un pipeline visuel et des fiches candidats détaillées.",
            "Les managers et RH collaborent sur le même espace, ce qui fluidifie les validations et réduit le time-to-hire."
          ],
          bullets: [
            'Pipeline de recrutement par poste',
            'Fiches candidats avec scoring',
            'Suivi des entretiens et feedbacks'
          ]
        },
        {
          title: 'Standardiser l\'onboarding',
          paragraphs: [
            "Les premières semaines d'un collaborateur sont déterminantes. Notion permet de créer un parcours d'intégration structuré avec des checklists par jour, par semaine et par mois.",
            "Chaque nouvelle recrue dispose d'un espace personnalisé avec toutes les ressources nécessaires."
          ]
        },
        {
          title: 'Piloter la QVCT et les indicateurs RH',
          paragraphs: [
            "Le bien-être au travail est un enjeu stratégique. Notion centralise les enquêtes de satisfaction, le suivi des risques psychosociaux et les plans d'action QVCT.",
            "Les indicateurs RH (turnover, absentéisme, eNPS) sont suivis dans un tableau de bord clair qui éclaire les décisions."
          ]
        }
      ],
      templateLinkIds: [
        'qualite-vie-travail-rps-qvct',
        'gestion-complete-intermittence-artiste-comediens-techniciens'
      ],
      articleLinkSlugs: []
    },
    templateIds: [
      'qualite-vie-travail-rps-qvct',
      'gestion-complete-intermittence-artiste-comediens-techniciens'
    ],
    articleSlugs: [],
    seo: {
      title: 'Template Notion RH | Recrutement, onboarding et QVCT',
      description:
        'Structurez vos processus RH avec Notion : recrutement, onboarding, gestion des talents et QVCT. Template sur mesure pour DRH.',
      keywords: [
        'notion ressources humaines',
        'template notion rh',
        'recrutement notion',
        'onboarding notion',
        'qvct notion'
      ],
      openGraph: {
        title: 'Notion pour les ressources humaines',
        description:
          'Centralisez vos processus RH avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/qualite_vie_travail_rps_qvct.jpg'
      }
    },
    customCta: {
      title: 'Un système RH Notion adapté à votre organisation',
      description: 'De 10 à 500 collaborateurs, nous créons un espace RH sur mesure : recrutement, onboarding, talents et QVCT.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Ressources humaines'
    },
    category: 'metier'
  },
  {
    id: 'infirmiers',
    slug: 'infirmiers',
    path: '/notion-infirmiers',
    title: 'Notion pour les infirmiers libéraux',
    subtitle:
      'Organisez vos tournées, votre patientèle et votre administratif dans un espace simple.',
    description:
      'Les infirmiers libéraux et professions de santé gèrent planning, patients et comptabilité. Notion simplifie le quotidien.',
    heroImage: '/images/templates/guide_fiv_parcours_fecondation_in_vitro.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les infirmiers libéraux cumulent soins, administratif et gestion sans avoir d\'outil adapté à leur réalité terrain.',
      bullets: [
        'Planning de tournées géré sur papier ou agenda personnel',
        'Suivi des patients fragmenté entre carnet et logiciel de facturation',
        'Obligations administratives (URSSAF, CPAM) chronophages'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion offre un espace accessible sur mobile pour gérer patients, planning et administratif en un seul endroit.',
      bullets: [
        'Fiches patients avec historique des soins',
        'Planning de tournée optimisé',
        'Suivi des cotisations et échéances administratives'
      ]
    },
    longForm: {
      intro: [
        "L'infirmier libéral est un entrepreneur de santé. Au-delà des soins, il doit gérer sa patientèle, ses tournées, sa facturation, ses formations DPC et ses obligations administratives. Tout cela avec un temps limité et souvent depuis son véhicule.",
        "Notion, accessible sur smartphone et tablette, permet de centraliser ces informations dans un espace simple et consultable partout. Un template sur mesure s'adapte à votre pratique."
      ],
      sections: [
        {
          title: 'Gérer la patientèle',
          paragraphs: [
            "Chaque patient a un parcours de soins, des prescriptions et un historique. Notion permet de créer des fiches patients claires, reliées aux actes réalisés et aux documents importants.",
            "Vous retrouvez instantanément les informations d'un patient sans fouiller dans des dossiers papier."
          ],
          bullets: [
            'Fiches patients avec antécédents',
            'Suivi des prescriptions et actes',
            'Alertes sur les renouvellements'
          ]
        },
        {
          title: 'Optimiser les tournées',
          paragraphs: [
            "Une tournée bien organisée fait gagner du temps et de l'énergie. Notion permet de planifier les visites, de regrouper par secteur géographique et de visualiser la charge de travail.",
            "Les remplacements et les urgences sont intégrés facilement dans le planning."
          ]
        },
        {
          title: 'Simplifier l\'administratif',
          paragraphs: [
            "URSSAF, CPAM, CARPIMKO, DPC : les obligations sont nombreuses. Notion centralise toutes les échéances et les documents nécessaires.",
            "Vous ne manquez plus aucune date limite et retrouvez vos justificatifs en quelques secondes."
          ]
        }
      ],
      templateLinkIds: [
        'guide-fiv-parcours-fecondation-in-vitro'
      ],
      articleLinkSlugs: [
        'parcours-patient-fiv'
      ]
    },
    templateIds: [
      'guide-fiv-parcours-fecondation-in-vitro'
    ],
    articleSlugs: [
      'parcours-patient-fiv'
    ],
    seo: {
      title: 'Template Notion Infirmier Libéral | Patients, tournées et admin',
      description:
        'Organisez votre activité d\'infirmier libéral avec Notion : fiches patients, planning de tournées et suivi administratif. Template sur mesure.',
      keywords: [
        'notion infirmier libéral',
        'template notion infirmier',
        'gestion cabinet infirmier notion',
        'planning tournées infirmier',
        'suivi patients notion'
      ],
      openGraph: {
        title: 'Notion pour les infirmiers libéraux',
        description:
          'Simplifiez votre quotidien d\'infirmier libéral avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/guide_fiv_parcours_fecondation_in_vitro.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion pensé pour votre pratique de santé',
      description: 'Infirmier, kiné, sage-femme ou orthophoniste : nous créons un espace adapté à votre pratique libérale.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Infirmier libéral / Profession de santé'
    },
    category: 'metier'
  },
  {
    id: 'vtc',
    slug: 'vtc',
    path: '/notion-vtc',
    title: 'Notion pour les chauffeurs VTC',
    subtitle:
      'Suivez vos courses, vos revenus et votre administratif depuis un tableau de bord unique.',
    description:
      'Les chauffeurs VTC et taxis ont besoin de visibilité sur leur activité. Notion centralise courses, finances et obligations.',
    heroImage: '/images/templates/vtc_pro_manager_crm_gestion_chauffeur_vtc_taxi.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les chauffeurs VTC gèrent leur activité avec des outils disparates et manquent de visibilité sur leur rentabilité.',
      bullets: [
        'Revenus suivis sur tableur sans analyse automatique',
        'Dépenses (carburant, entretien, assurance) difficiles à consolider',
        'Obligations administratives (carte VTC, visite médicale) oubliées'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion offre un cockpit de gestion complet pour les chauffeurs indépendants, accessible depuis le téléphone.',
      bullets: [
        'Suivi des courses et revenus par plateforme',
        'Calcul automatique de la rentabilité',
        'Rappels sur les échéances administratives'
      ]
    },
    longForm: {
      intro: [
        "Être chauffeur VTC, c'est être chef d'entreprise. Au-delà de la conduite, il faut gérer ses revenus, ses charges, sa fiscalité, l'entretien du véhicule et les obligations réglementaires. Sans outil adapté, on navigue à vue.",
        "Notion permet de créer un véritable tableau de bord de gestion pour votre activité VTC. Un template sur mesure s'adapte à vos plateformes, votre statut juridique et vos objectifs."
      ],
      sections: [
        {
          title: 'Suivre les revenus et la rentabilité',
          paragraphs: [
            "Uber, Bolt, Heetch, courses directes : les sources de revenus sont multiples. Notion permet de centraliser toutes les courses et d'analyser la rentabilité par plateforme, par jour et par zone.",
            "Vous savez exactement combien vous gagnez net après charges et pouvez optimiser vos horaires et vos zones de travail."
          ],
          bullets: [
            'Revenus par plateforme et par période',
            'Suivi des charges fixes et variables',
            'Calcul du revenu net réel'
          ]
        },
        {
          title: 'Gérer le véhicule et les dépenses',
          paragraphs: [
            "Le véhicule est votre outil de travail. Notion centralise carburant, entretien, assurance, leasing et permet de planifier les échéances.",
            "Vous anticipez les dépenses et gardez un historique propre pour votre comptable."
          ]
        },
        {
          title: 'Rester en règle',
          paragraphs: [
            "Carte professionnelle VTC, visite médicale, contrôle technique, assurance RC Pro : les obligations sont nombreuses. Notion crée un calendrier de rappels pour ne rien oublier.",
            "En cas de contrôle, tous vos documents sont accessibles depuis votre téléphone."
          ]
        }
      ],
      templateLinkIds: [
        'vtc-pro-manager-crm-gestion-chauffeur-vtc-taxi'
      ],
      articleLinkSlugs: []
    },
    templateIds: [
      'vtc-pro-manager-crm-gestion-chauffeur-vtc-taxi',
      'crm-freelance-gratuit-gestion-autoentrepreneur'
    ],
    articleSlugs: [],
    seo: {
      title: 'Template Notion VTC | Gestion courses, revenus et admin',
      description:
        'Gérez votre activité VTC avec Notion : suivi des courses, revenus par plateforme, dépenses et obligations administratives. Template sur mesure.',
      keywords: [
        'notion vtc',
        'template notion chauffeur vtc',
        'gestion vtc notion',
        'suivi courses vtc',
        'revenus chauffeur vtc notion'
      ],
      openGraph: {
        title: 'Notion pour les chauffeurs VTC',
        description:
          'Pilotez votre activité VTC avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/vtc_pro_manager_crm_gestion_chauffeur_vtc_taxi.jpg'
      }
    },
    customCta: {
      title: 'Un tableau de bord VTC conçu pour votre activité',
      description: 'VTC, taxi ou LOTI : nous créons un espace Notion adapté à vos plateformes, votre statut et vos objectifs de revenus.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Chauffeur VTC / Taxi'
    },
    category: 'metier'
  },
  {
    id: 'professeurs',
    slug: 'professeurs',
    path: '/notion-professeurs',
    title: 'Notion pour les professeurs',
    subtitle:
      'Planifiez vos cours, suivez vos élèves et organisez vos ressources pédagogiques.',
    description:
      'Les enseignants gèrent cours, évaluations, projets et administratif. Notion devient le cahier de bord numérique idéal.',
    heroImage: '/images/templates/dashboard_formateur.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les enseignants manquent d\'outils centralisés pour gérer la complexité croissante de leur métier.',
      bullets: [
        'Préparations de cours dispersées entre fichiers et clés USB',
        'Suivi des élèves difficile sans outil dédié',
        'Charge administrative croissante (bulletins, conseils, projets)'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion offre un espace modulaire pour organiser cours, évaluations et suivi des élèves, accessible partout.',
      bullets: [
        'Cahier de textes numérique structuré',
        'Base de ressources pédagogiques classées',
        'Suivi des compétences par élève'
      ]
    },
    longForm: {
      intro: [
        "Le métier d'enseignant ne se résume pas aux heures de cours. Préparation, évaluation, suivi individualisé, projets pédagogiques, réunions, conseils de classe : la charge de travail dépasse largement le temps devant les élèves.",
        "Notion permet de structurer toutes ces dimensions dans un espace unique. Un template sur mesure s'adapte à votre discipline, votre niveau et votre style pédagogique."
      ],
      sections: [
        {
          title: 'Organiser les cours et séquences',
          paragraphs: [
            "Chaque séquence pédagogique a ses objectifs, ses ressources et ses évaluations. Notion permet de planifier l'année avec une progression claire, reliée aux programmes officiels.",
            "Vous retrouvez vos fiches de cours d'une année sur l'autre et les améliorez progressivement."
          ],
          bullets: [
            'Progression annuelle par séquence',
            'Fiches de cours avec objectifs et ressources',
            'Lien avec les compétences du programme'
          ]
        },
        {
          title: 'Suivre les élèves',
          paragraphs: [
            "Le suivi individualisé est une exigence croissante. Notion permet de créer un profil par élève avec notes, observations, PAP/PPRE et échanges avec les familles.",
            "Lors des conseils de classe, vous avez toutes les informations à portée de main."
          ]
        },
        {
          title: 'Gérer les projets et l\'administratif',
          paragraphs: [
            "Sorties scolaires, projets interdisciplinaires, certifications : les projets s'accumulent. Notion centralise la planification, les autorisations et le suivi.",
            "L'administratif (bulletins, fiches de vœux, rapports) est également structuré pour gagner du temps sur les tâches répétitives."
          ]
        }
      ],
      templateLinkIds: [
        'dashboard-formateur',
        'guide-parcoursup-parents-2027'
      ],
      articleLinkSlugs: []
    },
    templateIds: [
      'dashboard-formateur',
      'guide-parcoursup-parents-2027',
      'qualiopi-systeme-qualite'
    ],
    articleSlugs: [],
    seo: {
      title: 'Template Notion Professeur | Cours, élèves et pédagogie',
      description:
        'Organisez votre activité d\'enseignant avec Notion : cours, séquences, suivi des élèves et projets pédagogiques. Template sur mesure.',
      keywords: [
        'notion professeur',
        'template notion enseignant',
        'organisation cours notion',
        'suivi élèves notion',
        'cahier de bord enseignant notion'
      ],
      openGraph: {
        title: 'Notion pour les professeurs',
        description:
          'Structurez votre enseignement avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/dashboard_formateur.jpg'
      }
    },
    customCta: {
      title: 'Un espace Notion conçu pour votre discipline',
      description: 'Primaire, collège, lycée ou supérieur : nous créons un template adapté à votre matière et votre style pédagogique.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Professeur / Enseignant'
    },
    category: 'metier'
  },
  {
    id: 'etudiants',
    slug: 'etudiants',
    path: '/notion-etudiants',
    title: 'Notion pour les étudiants',
    subtitle:
      'Organisez vos cours, vos révisions et vos projets pour réussir vos études.',
    description:
      'Les étudiants ont besoin de structure pour gérer cours, révisions, stages et projets. Notion devient le compagnon idéal.',
    heroImage: '/images/templates/systeme_second_cerveau_francais.jpg',
    problem: {
      title: 'Le problème',
      description:
        'Les étudiants jonglent entre cours, révisions, projets de groupe et recherche de stage sans méthode claire.',
      bullets: [
        'Notes de cours dispersées entre cahiers, PDF et photos',
        'Pas de planning de révision structuré',
        'Projets de groupe mal coordonnés'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion centralise cours, fiches de révision et planning dans un espace gratuit et accessible sur tous les appareils.',
      bullets: [
        'Prise de notes structurée par matière',
        'Planning de révision avec rappels',
        'Suivi des candidatures de stage'
      ]
    },
    longForm: {
      intro: [
        "Réussir ses études demande plus que de l'intelligence : il faut de l'organisation. Entre les cours magistraux, les TD, les projets, les stages et la vie associative, les étudiants qui ont un système performant prennent l'avantage.",
        "Notion est gratuit pour les étudiants et offre un espace flexible pour tout organiser. Un template sur mesure peut transformer votre productivité académique."
      ],
      sections: [
        {
          title: 'Structurer ses cours et ses notes',
          paragraphs: [
            "Un bon système de notes permet de retrouver n'importe quelle information en quelques secondes. Notion organise vos notes par matière, par semestre et par type (cours, TD, recherches).",
            "Les fiches de révision sont reliées aux cours source, ce qui facilite les révisions et la préparation des examens."
          ],
          bullets: [
            'Notes classées par matière et semestre',
            'Fiches de révision avec méthode active recall',
            'Liens entre concepts et ressources'
          ]
        },
        {
          title: 'Planifier ses révisions',
          paragraphs: [
            "Un planning de révision bien fait est la clé de la réussite aux examens. Notion permet de créer un calendrier par matière avec des objectifs quotidiens et hebdomadaires.",
            "Vous visualisez votre progression et ajustez votre effort en fonction des résultats."
          ]
        },
        {
          title: 'Gérer stages et projets',
          paragraphs: [
            "La recherche de stage exige un suivi rigoureux : candidatures envoyées, relances, entretiens, réponses. Notion crée un pipeline clair pour ne rien oublier.",
            "Les projets de groupe bénéficient aussi d'un espace partagé : répartition des tâches, deadlines, documents et comptes rendus."
          ]
        }
      ],
      templateLinkIds: [
        'systeme-second-cerveau',
        'guide-parcoursup-parents-2027'
      ],
      articleLinkSlugs: []
    },
    templateIds: [
      'systeme-second-cerveau',
      'guide-parcoursup-parents-2027',
      'content-creator-hub'
    ],
    articleSlugs: [],
    seo: {
      title: 'Template Notion Étudiant | Cours, révisions et projets',
      description:
        'Organisez vos études avec Notion : notes de cours, fiches de révision, planning d\'examens et suivi de stage. Template sur mesure étudiant.',
      keywords: [
        'notion étudiant',
        'template notion étudiant',
        'organisation études notion',
        'fiches de révision notion',
        'planning révision notion'
      ],
      openGraph: {
        title: 'Notion pour les étudiants',
        description:
          'Boostez votre réussite académique avec un template Notion sur mesure.',
        type: 'website',
        image: '/images/templates/systeme_second_cerveau_francais.jpg'
      }
    },
    customCta: {
      title: 'Un espace Notion taillé pour votre cursus',
      description: 'Prépa, licence, master ou école de commerce : nous créons un template adapté à votre formation et vos objectifs.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Étudiant'
    },
    category: 'metier'
  },
  // ──────────────────────────────────────────────────────────
  // Pages BTP — portes d'entrée SEO métier
  // ──────────────────────────────────────────────────────────
  {
    id: 'gestion-chantier-artisan',
    slug: 'gestion-chantier-artisan',
    path: '/gestion-chantier-artisan',
    title: 'Gestion de chantier pour artisans du bâtiment',
    subtitle:
      'Coordonnez vos chantiers, suivez vos clients et centralisez vos devis dans un espace unique.',
    description:
      'Les artisans du bâtiment jonglent entre chantiers simultanés, clients particuliers et sous-traitants. Notion structure le suivi sans alourdir le quotidien.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Un artisan qui gère plusieurs chantiers en même temps perd du temps à retrouver les infos : quel client a demandé quoi, où en est la commande, qui intervient demain.',
      bullets: [
        'Devis et avenants éparpillés entre email et papier',
        'Aucun suivi clair des interventions par client',
        'Coordination des sous-traitants gérée de tête'
      ]
    },
    notion: {
      title: 'Pourquoi Notion change la donne',
      description:
        'Un artisan organisé gagne des chantiers. Notion centralise clients, devis, plannings et comptes-rendus dans un espace accessible depuis n\'importe quel appareil.',
      bullets: [
        'Fiche chantier avec client, devis et avancement',
        'Planning hebdomadaire des interventions',
        'Suivi des commandes matériaux et fournisseurs'
      ]
    },
    longForm: {
      intro: [
        "Un artisan du bâtiment, qu'il soit peintre, carreleur, menuisier ou plaquiste, gère en permanence plusieurs chantiers en parallèle. Les clients appellent, les commandes arrivent, les sous-traitants doivent être coordonnés. Sans système, les informations se perdent et les erreurs coûtent cher.",
        "Notion permet de créer un tableau de bord simple et efficace, accessible depuis le chantier sur smartphone. Ce guide explique comment structurer votre activité d'artisan avec un template adapté à votre réalité terrain."
      ],
      sections: [
        {
          title: 'Centraliser les chantiers clients',
          paragraphs: [
            "Chaque chantier mérite une fiche dédiée : nom du client, adresse, descriptif des travaux, devis signé, avenants, photos avant/après. Notion relie tout ça dans un espace clair.",
            "Vous retrouvez l'historique d'un client en 10 secondes, même 2 ans après la fin des travaux. C'est aussi utile pour les garanties décennales et les SAV."
          ],
          bullets: [
            'Vue Kanban par statut : devis, en cours, terminé, SAV',
            'Photos de chantier et documents attachés',
            'Historique des échanges avec le client'
          ]
        },
        {
          title: 'Coordonner les intervenants et les commandes',
          paragraphs: [
            "Sur un chantier de rénovation, plusieurs corps de métier se succèdent. Notion permet de planifier les interventions, noter les dépendances et s'assurer que chaque artisan arrive au bon moment.",
            "Les commandes de matériaux sont liées aux chantiers : vous savez immédiatement ce qui a été commandé, chez quel fournisseur et pour quelle date de livraison."
          ]
        },
        {
          title: 'Suivre la facturation et les devis',
          paragraphs: [
            "Un artisan qui ne suit pas ses devis laisse de l'argent sur la table. Notion crée un pipeline commercial simple : devis envoyé, relancé, signé, facturé, payé.",
            "Vous avez une vision claire de votre carnet de commandes et anticipez les périodes creuses pour prospecter au bon moment."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template BTP a été conçu pour les artisans qui veulent structurer leur activité sans y passer des heures. Il centralise chantiers, clients, comptes-rendus de réunion et gestion des commissions dans un espace Notion prêt à l'emploi.",
            "Utilisé par plus de 150 entreprises du bâtiment, il s'adapte aux petits chantiers en solo comme aux projets avec plusieurs corps de métier. Accessible depuis n'importe quel appareil, y compris le smartphone sur le chantier."
          ],
          bullets: [
            'Base de données chantiers : statut, client, avancement',
            'Comptes-rendus de réunion structurés et horodatés',
            'Suivi des intervenants et des commissions par chantier',
            'Planning et jalons avec alertes',
            'Espace documents : devis, factures, photos avant/après'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'pilotage-copropriete-chantiers'],
    relatedPillarPaths: [
      '/suivi-chantier-electricien',
      '/organisation-chantier-plombier',
      '/gestion-chantier-macon',
      '/organiser-chantier-renovation',
      '/gestion-chantier-architecte'
    ],
    seo: {
      title: 'Gestion Chantier Artisan | Template Notion BTP',
      description:
        'Gérez vos chantiers, devis et clients particuliers avec Notion. Template BTP pour artisans du bâtiment : suivi interventions, coordination sous-traitants, facturation.',
      keywords: [
        'gestion chantier artisan',
        'suivi chantier artisan notion',
        'template notion artisan bâtiment',
        'organiser chantier artisan',
        'coordination artisans btp'
      ],
      openGraph: {
        title: 'Gestion de chantier pour artisans du bâtiment',
        description:
          'Centralisez vos chantiers, devis et clients avec un template Notion adapté aux artisans BTP.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion taillé pour votre métier d\'artisan',
      description: 'Peintre, menuisier, carreleur ou plaquiste : nous adaptons le template à votre activité et votre façon de travailler.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Artisan BTP'
    },
    category: 'metier'
  },
  {
    id: 'suivi-chantier-electricien',
    slug: 'suivi-chantier-electricien',
    path: '/suivi-chantier-electricien',
    title: 'Suivi de chantier pour électricien',
    subtitle:
      'Planifiez vos interventions, tracez votre matériel et gérez vos petits chantiers sans paperasse.',
    description:
      'Un électricien qui intervient sur de nombreux chantiers a besoin d\'un suivi rigoureux : planning, matériel, conformité. Notion structure tout depuis le terrain.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Les électriciens interviennent sur des chantiers de tailles variées, souvent en urgence. Sans suivi, les matériaux manquent, les plannings se chevauchent et la traçabilité des installations fait défaut.',
      bullets: [
        'Planning d\'interventions difficile à tenir avec plusieurs chantiers simultanés',
        'Stocks de matériel électrique non suivis, commandes en urgence',
        'Pas de traçabilité des installations pour les certificats de conformité'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est adapté',
      description:
        'Notion centralise les interventions, le matériel et les documents de conformité dans un espace accessible depuis le van ou le chantier.',
      bullets: [
        'Planning d\'interventions par semaine et par chantier',
        'Fiche chantier avec schémas et documents techniques',
        'Suivi du stock de matériel et commandes fournisseurs'
      ]
    },
    longForm: {
      intro: [
        "Un électricien professionnel intervient sur des chantiers très différents : maisons individuelles, appartements, locaux commerciaux, chantiers neufs ou rénovation. Chaque intervention a ses spécificités techniques, ses documents de conformité et son planning propre.",
        "Sans système de suivi, les informations s'accumulent sur des bons papier, dans des SMS et des mails. Notion permet de centraliser tout ça dans un espace simple, consultable depuis n'importe où."
      ],
      sections: [
        {
          title: 'Planifier les interventions électriques',
          paragraphs: [
            "La gestion du planning est le défi principal d'un électricien indépendant. Notion crée un calendrier d'interventions par chantier, avec les durées estimées, les dépendances et les ressources nécessaires.",
            "Vous anticipez les conflits de planning et optimisez vos déplacements. En cas d'urgence, vous visualisez immédiatement où vous avez de la disponibilité."
          ],
          bullets: [
            'Planning hebdomadaire par type d\'intervention',
            'Durée estimée vs réelle par chantier',
            'Alertes sur les délais à respecter (CONSUEL, EDF)'
          ]
        },
        {
          title: 'Suivre le matériel et les commandes',
          paragraphs: [
            "Un chantier électrique bien géré commence par une bonne gestion du matériel. Notion liste les fournitures par chantier, les commandes en cours et les livraisons attendues.",
            "Vous évitez les ruptures qui bloquent un chantier et comparez facilement vos fournisseurs sur prix et délai."
          ]
        },
        {
          title: 'Gérer la conformité et les documents techniques',
          paragraphs: [
            "Chaque installation électrique doit être documentée : schémas, certificats CONSUEL, fiches techniques des équipements. Notion archive ces documents par chantier.",
            "En cas de litige ou d'intervention ultérieure, vous retrouvez immédiatement les plans et les certificats. C'est aussi un argument de sérieux auprès de vos clients."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template BTP permet aux électriciens de centraliser leurs chantiers, interventions, documents techniques et suivi matériel dans un seul espace Notion. Plus de 150 professionnels du bâtiment l'utilisent déjà.",
            "Il s'adapte aussi bien aux électriciens indépendants qui gèrent 3 chantiers en parallèle qu'aux PME avec plusieurs techniciens sur le terrain."
          ],
          bullets: [
            'Fiches chantier avec planning, matériel et documents',
            'Comptes-rendus d\'intervention structurés',
            'Suivi des commandes et livraisons fournisseurs',
            'Espace certificats : CONSUEL, attestations, schémas',
            'Vue calendrier des interventions à venir'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'gestion-projet-notion-guide-complet'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/organisation-chantier-plombier',
      '/gestion-chantier-macon',
      '/organiser-chantier-renovation',
      '/gestion-chantier-architecte'
    ],
    seo: {
      title: 'Suivi Chantier Électricien | Template Notion BTP',
      description:
        'Suivez vos chantiers électriques avec Notion : planning interventions, stock matériel, conformité CONSUEL. Template BTP pour électriciens.',
      keywords: [
        'suivi chantier électricien',
        'planning interventions électricien',
        'gestion chantier électricien notion',
        'template notion électricien',
        'suivi matériel électrique chantier'
      ],
      openGraph: {
        title: 'Suivi de chantier pour électricien',
        description:
          'Gérez vos interventions, matériel et conformité avec un template Notion pour électriciens.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un espace Notion pensé pour votre activité d\'électricien',
      description: 'Petits dépannages ou grands chantiers tertiaires : nous créons un template adapté à votre type d\'interventions et votre organisation.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Électricien BTP'
    },
    category: 'metier'
  },
  {
    id: 'organisation-chantier-plombier',
    slug: 'organisation-chantier-plombier',
    path: '/organisation-chantier-plombier',
    title: 'Organisation chantier plombier',
    subtitle:
      'Gérez vos dépannages, vos chantiers de rénovation et vos commandes depuis un seul espace.',
    description:
      'Entre les urgences, les chantiers planifiés et la gestion du stock, un plombier a besoin d\'un système simple et efficace. Notion s\'adapte à votre rythme terrain.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Un plombier gère deux types d\'activité très différents : les urgences imprévues et les chantiers de rénovation planifiés. Sans organisation, les deux se perturbent mutuellement.',
      bullets: [
        'Urgences qui désorganisent les chantiers planifiés',
        'Commandes de pièces passées à la dernière minute',
        'Historique des interventions difficile à retrouver pour les garanties'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion sépare clairement dépannages et chantiers, centralise l\'historique client et suit les commandes de pièces pour que rien ne manque au bon moment.',
      bullets: [
        'Distinction urgences / chantiers planifiés',
        'Historique des interventions par client et adresse',
        'Suivi des commandes pièces et délais fournisseurs'
      ]
    },
    longForm: {
      intro: [
        "La plomberie est un métier où l'organisation fait toute la différence. Un plombier bien organisé honore ses rendez-vous, arrive avec les bonnes pièces et garde une traçabilité de toutes ses interventions. Un plombier désorganisé perd des heures à chercher ses informations et dépense trop en commandes d'urgence.",
        "Notion permet de structurer les deux faces du métier : la gestion des dépannages urgents et le pilotage des chantiers de rénovation. Ce guide montre comment mettre en place un système simple et efficace."
      ],
      sections: [
        {
          title: 'Gérer les dépannages et urgences',
          paragraphs: [
            "Les dépannages sont la colonne vertébrale d'une activité de plombier. Notion crée une file d'interventions urgentes, triées par priorité et zone géographique.",
            "Chaque dépannage est documenté : problème signalé, diagnostic posé, pièces utilisées, temps passé. C'est la base d'une facturation précise et d'un historique client utile."
          ],
          bullets: [
            'File d\'urgences triée par priorité et secteur',
            'Fiche intervention avec diagnostic et pièces utilisées',
            'Suivi du temps passé pour la facturation'
          ]
        },
        {
          title: 'Piloter les chantiers de rénovation',
          paragraphs: [
            "Un chantier de rénovation salle de bain ou cuisine implique plusieurs phases, des délais de livraison et souvent d'autres artisans. Notion crée un planning par phase avec les dépendances.",
            "Le client suit l'avancement et vous êtes couvert en cas de désaccord : chaque décision est tracée."
          ]
        },
        {
          title: 'Anticiper les commandes de pièces',
          paragraphs: [
            "La pièce qui manque bloque un chantier et coûte cher en retour chez le fournisseur. Notion centralise les besoins en matériaux par chantier et génère une liste de commandes consolidée.",
            "Vous comparez facilement les fournisseurs et suivez les délais pour ne jamais être pris de court."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template Notion centralise tout ce qu'un plombier a besoin de suivre : chantiers en cours, dépannages, commandes de pièces et historique client. Il remplace les carnets papier et les tableurs incomplets.",
            "Accessible depuis votre téléphone entre deux interventions, il transforme la gestion de votre activité en quelques minutes de prise en main."
          ],
          bullets: [
            'File de dépannages triée par priorité et secteur',
            'Chantiers de rénovation avec phases et avancement',
            'Historique des interventions par client et adresse',
            'Commandes pièces liées aux chantiers',
            'Suivi devis, factures et règlements'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'gestion-projet-notion-guide-complet'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/suivi-chantier-electricien',
      '/gestion-chantier-macon',
      '/organiser-chantier-renovation',
      '/gestion-chantier-architecte'
    ],
    seo: {
      title: 'Organisation Chantier Plombier | Template Notion BTP',
      description:
        'Organisez vos interventions plomberie avec Notion : dépannages, chantiers rénovation, commandes pièces. Template BTP pour plombiers.',
      keywords: [
        'organisation chantier plombier',
        'gestion interventions plombier notion',
        'suivi chantier plombier',
        'template notion plombier',
        'planning travaux plomberie'
      ],
      openGraph: {
        title: 'Organisation chantier plombier',
        description:
          'Gérez dépannages et chantiers de rénovation avec un template Notion pour plombiers.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion adapté à votre activité de plombier',
      description: 'Dépannage d\'urgence, rénovation complète ou neuf : nous configurons le template selon votre façon de travailler.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Plombier BTP'
    },
    category: 'metier'
  },
  {
    id: 'gestion-chantier-macon',
    slug: 'gestion-chantier-macon',
    path: '/gestion-chantier-macon',
    title: 'Gestion de chantier maçon',
    subtitle:
      'Suivez l\'avancement de vos chantiers de maçonnerie, coordonnez votre équipe et documentez chaque phase.',
    description:
      'La maçonnerie implique des chantiers longs, une coordination d\'équipe et un suivi rigoureux du gros œuvre. Notion donne une vision claire de chaque chantier.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Un chantier de maçonnerie dure souvent plusieurs semaines. Sans suivi structuré, les retards s\'accumulent, la coordination avec les autres corps de métier se dégrade et les responsabilités deviennent floues.',
      bullets: [
        'Avancement du gros œuvre difficile à communiquer au client',
        'Coordination équipe et sous-traitants gérée oralement',
        'Comptes-rendus de chantier inexistants ou perdus'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est pertinent',
      description:
        'Notion structure chaque phase du chantier de maçonnerie, centralise les comptes-rendus et donne à toute l\'équipe une vision partagée de l\'avancement.',
      bullets: [
        'Suivi d\'avancement par phase : fondations, élévation, couverture',
        'Comptes-rendus de chantier horodatés et photos',
        'Tableau de bord pour le chef de chantier et le client'
      ]
    },
    longForm: {
      intro: [
        "La maçonnerie est l'épine dorsale d'un chantier de construction ou de rénovation lourde. Elle conditionne toutes les interventions suivantes : électricité, plomberie, isolation. Un retard en maçonnerie, c'est toute la chaîne qui se décale.",
        "Un maçon organisé documente l'avancement, communique clairement avec le client et coordonne efficacement son équipe. Notion rend cette organisation accessible sans formation technique particulière."
      ],
      sections: [
        {
          title: 'Suivre l\'avancement phase par phase',
          paragraphs: [
            "Chaque chantier de maçonnerie se décompose en phases clairement identifiées : terrassement, fondations, élévation des murs, dalle, couverture. Notion crée un tableau de bord visuel avec le statut de chaque phase.",
            "Le client voit l'avancement en temps réel. Le chef de chantier sait ce qui est fait, ce qui est en cours et ce qui bloque."
          ],
          bullets: [
            'Phases de chantier avec dates planifiées et réelles',
            'Photos d\'avancement attachées à chaque phase',
            'Indicateur de retard ou d\'avance sur planning'
          ]
        },
        {
          title: 'Coordonner l\'équipe et les sous-traitants',
          paragraphs: [
            "Un chantier de maçonnerie mobilise souvent plusieurs ouvriers et des sous-traitants spécialisés (ferrailleur, coffreur, grutier). Notion planifie les présences et les besoins en équipement.",
            "Les consignes sont écrites et accessibles à tous depuis le smartphone sur le chantier. Plus d'information transmise de bouche à oreille et oubliée."
          ]
        },
        {
          title: 'Documenter pour se protéger',
          paragraphs: [
            "En maçonnerie, la documentation est une protection. Réserves émises, modifications demandées par le client, décisions prises en réunion : tout doit être tracé.",
            "Notion archive les comptes-rendus de chantier, les avenants et les photos de réception partielle. En cas de litige, vous avez des preuves claires."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template BTP donne aux maçons et chefs de chantier une vision claire de chaque projet : phases d'avancement, équipe mobilisée, comptes-rendus et documents. Conçu pour le terrain, il se complète depuis un smartphone.",
            "Plus de 150 entreprises du BTP l'utilisent pour professionnaliser leur suivi de chantier et réduire les litiges en fin de projet."
          ],
          bullets: [
            'Suivi d\'avancement par phase : fondations, élévation, dalle',
            'Gestion de l\'équipe et des sous-traitants par chantier',
            'Comptes-rendus de chantier avec photos horodatées',
            'Avenants et modifications documentés',
            'Tableau de bord chef de chantier avec alertes retards'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'pilotage-copropriete-chantiers'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/suivi-chantier-electricien',
      '/organisation-chantier-plombier',
      '/organiser-chantier-renovation',
      '/gestion-chantier-architecte'
    ],
    seo: {
      title: 'Gestion Chantier Maçon | Template Notion BTP',
      description:
        'Gérez vos chantiers de maçonnerie avec Notion : avancement gros œuvre, coordination équipe, comptes-rendus. Template BTP pour maçons.',
      keywords: [
        'gestion chantier maçon',
        'suivi chantier maçonnerie notion',
        'planning travaux maçon',
        'template notion maçon',
        'suivi gros oeuvre notion'
      ],
      openGraph: {
        title: 'Gestion de chantier maçon',
        description:
          'Pilotez vos chantiers de maçonnerie avec un template Notion structuré par phase.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion conçu pour votre activité de maçonnerie',
      description: 'Gros œuvre neuf, rénovation lourde ou extension : nous adaptons le template à votre type de chantiers et votre taille d\'équipe.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Maçon BTP'
    },
    category: 'metier'
  },
  {
    id: 'organiser-chantier-renovation',
    slug: 'organiser-chantier-renovation',
    path: '/organiser-chantier-renovation',
    title: 'Organisation chantier rénovation',
    subtitle:
      'Coordonnez les corps de métier, suivez l\'avancement des travaux et tenez votre planning de rénovation.',
    description:
      'Un chantier de rénovation implique de nombreux intervenants, des délais serrés et un budget à maîtriser. Notion centralise tout pour éviter les mauvaises surprises.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'La rénovation est le chantier le plus complexe à piloter : plusieurs corps de métier se succèdent, chaque retard en décale un autre et le client est souvent stressé par les délais.',
      bullets: [
        'Planning multi-corps de métier difficile à tenir',
        'Suivi du budget rénovation dispersé entre devis et factures',
        'Communication avec le client consommatrice de temps'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est efficace',
      description:
        'Notion crée un tableau de bord unique pour suivre tous les corps de métier, le budget et l\'avancement d\'un chantier de rénovation de A à Z.',
      bullets: [
        'Planning des corps de métier avec dépendances',
        'Suivi budgétaire devis / factures / reste à payer',
        'Espace de communication partagé avec le client'
      ]
    },
    longForm: {
      intro: [
        "Organiser un chantier rénovation, c'est orchestrer une succession d'interventions dont chacune conditionne la suivante. Le démolisseur passe avant le maçon, le maçon avant l'électricien, l'électricien avant le plaquiste, le plaquiste avant le peintre. Un retard à n'importe quel niveau cascade sur toute la chaîne.",
        "Notion permet de créer un planning de rénovation visuel, de suivre l'avancement par lot de travaux et de communiquer efficacement avec le client et les artisans. Ce guide explique comment s'organiser pour mener une rénovation sans stress inutile."
      ],
      sections: [
        {
          title: 'Planifier la coordination des corps de métier',
          paragraphs: [
            "La clé d'un chantier de rénovation réussi est un planning qui tient compte des dépendances entre lots. Notion permet de créer ce planning en Gantt ou Kanban, avec les dates de début et de fin de chaque lot.",
            "Quand un artisan prend du retard, vous visualisez immédiatement l'impact sur la suite et vous pouvez notifier les intervenants suivants sans attendre."
          ],
          bullets: [
            'Planning par lot : démolition, gros œuvre, électricité, plomberie, finitions',
            'Dépendances entre corps de métier visibles',
            'Alertes sur les retards et impacts en cascade'
          ]
        },
        {
          title: 'Suivre le budget travaux maison',
          paragraphs: [
            "Une rénovation dépasse rarement son budget par accident : c'est souvent par manque de suivi. Notion centralise les devis acceptés, les factures reçues et les avenants de travaux.",
            "Vous voyez en temps réel où vous en êtes par rapport au budget initial et vous pouvez prendre des décisions éclairées sur les éventuels extras."
          ]
        },
        {
          title: 'Communiquer avec le client',
          paragraphs: [
            "Un client bien informé est un client serein. Notion permet de créer un espace de suivi partagé avec des photos d'avancement, des comptes-rendus de visite et un planning mis à jour.",
            "Vous réduisez les appels d'inquiétude et renforcez la confiance, ce qui favorise les recommandations."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template a été pensé pour les chantiers de rénovation multi-corps de métier. Il structure le planning par lot, le suivi budgétaire et la communication avec le client dans un seul espace Notion.",
            "Conducteurs de travaux, maîtres d'œuvre et particuliers qui pilotent leur propre rénovation l'utilisent pour garder la maîtrise de leur projet de A à Z."
          ],
          bullets: [
            'Planning par lot avec dépendances entre corps de métier',
            'Suivi budget : devis acceptés, factures reçues, reste à payer',
            'Espace de suivi partagé avec le client',
            'Comptes-rendus de visite de chantier',
            'Réserves et levées de réserves par entreprise'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template', 'pilotage-copropriete-chantiers']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'pilotage-copropriete-chantiers'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/suivi-chantier-electricien',
      '/organisation-chantier-plombier',
      '/gestion-chantier-macon',
      '/gestion-chantier-architecte'
    ],
    seo: {
      title: 'Organiser Chantier Rénovation | Template Notion BTP',
      description:
        'Organisez votre chantier rénovation avec Notion : planning travaux, coordination corps de métier, suivi budget. Template BTP pour rénovation maison.',
      keywords: [
        'organiser chantier rénovation',
        'suivi travaux maison notion',
        'planning rénovation notion',
        'coordination corps de métier rénovation',
        'template notion rénovation'
      ],
      openGraph: {
        title: 'Organisation chantier rénovation',
        description:
          'Coordonnez tous les corps de métier et suivez votre budget rénovation avec Notion.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion pour piloter votre chantier de rénovation',
      description: 'Rénovation complète, extension ou simple rafraîchissement : nous créons un espace Notion adapté à la complexité de votre projet.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Chantier rénovation'
    },
    category: 'metier'
  },
  {
    id: 'gestion-chantier-architecte',
    slug: 'gestion-chantier-architecte',
    path: '/gestion-chantier-architecte',
    title: 'Gestion de chantier architecte',
    subtitle:
      'Centralisez vos réunions de chantier, comptes-rendus et coordination MOE dans un espace structuré.',
    description:
      'L\'architecte en maîtrise d\'œuvre jongle entre réunions de chantier, comptes-rendus, levées de réserves et coordination avec le maître d\'ouvrage. Notion structure ce pilotage.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'La maîtrise d\'œuvre exige une traçabilité irréprochable : chaque décision prise en réunion de chantier doit être documentée, distribuée et suivie.',
      bullets: [
        'Comptes-rendus de réunion long à rédiger et difficiles à partager',
        'Levées de réserves non tracées, risques juridiques en fin de chantier',
        'Coordination MOE / MOA / entreprises dispersée entre emails'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est adapté à la MOE',
      description:
        'Notion structure les réunions de chantier, automatise la diffusion des comptes-rendus et suit chaque réserve jusqu\'à sa levée.',
      bullets: [
        'Comptes-rendus de réunion structurés et datés',
        'Suivi des réserves par lot et par entreprise',
        'Tableau de bord MOE partagé avec le maître d\'ouvrage'
      ]
    },
    longForm: {
      intro: [
        "Un architecte en charge d'une maîtrise d'œuvre endosse une responsabilité considérable : coordonner les entreprises, garantir la conformité aux plans et documenter chaque décision. Les réunions de chantier hebdomadaires produisent des comptes-rendus qui engagent juridiquement toutes les parties.",
        "Notion permet de structurer ce travail de documentation et de coordination sans multiplier les outils. Un template adapté à la maîtrise d'œuvre change la qualité du suivi et réduit les risques en fin de chantier."
      ],
      sections: [
        {
          title: 'Structurer les réunions de chantier',
          paragraphs: [
            "Chaque réunion de chantier suit un ordre du jour type : avancement par lot, points bloquants, décisions prises, réserves émises. Notion crée un template de compte-rendu qui se complète en réunion, directement depuis la tablette.",
            "Le CR est distribué à tous les participants en quelques clics depuis Notion. Plus de re-saisie, plus de perte de temps en rédaction post-réunion."
          ],
          bullets: [
            'Template de CR pré-structuré avec ordre du jour type',
            'Participants, présents et diffusion documentés',
            'Actions à suivre extraites automatiquement du CR'
          ]
        },
        {
          title: 'Suivre les réserves et levées de réserves',
          paragraphs: [
            "Les réserves émises en réception de chantier sont souvent mal suivies. Notion crée une base de données des réserves par lot, par entreprise et par délai de levée.",
            "Vous savez à tout moment combien de réserves restent ouvertes et par qui. C'est une protection essentielle pour la responsabilité décennale de l'architecte."
          ]
        },
        {
          title: 'Coordonner MOE, MOA et entreprises',
          paragraphs: [
            "La communication entre le maître d'ouvrage, l'architecte et les entreprises est source de malentendus. Notion centralise les échanges officiels, les validations et les demandes de modification.",
            "Chaque décision est tracée avec sa date et son auteur. En cas de litige, l'historique est complet et inattaquable."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template a été conçu précisément pour la maîtrise d'œuvre : réunions de chantier structurées, comptes-rendus diffusables en un clic, suivi des réserves jusqu'à leur levée. C'est l'outil de documentation que tout architecte MOE devrait avoir.",
            "Il remplace les tableurs de réserves, les CR Word relancés par email et les fils de messages WhatsApp avec les entreprises. Plus de 150 professionnels du BTP l'ont adopté."
          ],
          bullets: [
            'Template de compte-rendu de réunion chantier',
            'Base de données des réserves par lot et entreprise',
            'Suivi des levées de réserves avec deadlines',
            'Tableau de bord MOE partageable avec le MOA',
            'Archivage des décisions et avenants par chantier'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template', 'prompts-ia-architecte-top-10']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'prompts-ia-architecte-top-10'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/suivi-chantier-electricien',
      '/organisation-chantier-plombier',
      '/gestion-chantier-macon',
      '/organiser-chantier-renovation'
    ],
    seo: {
      title: 'Gestion Chantier Architecte | Template Notion MOE',
      description:
        'Gérez votre maîtrise d\'œuvre avec Notion : réunions de chantier, comptes-rendus, suivi réserves, coordination MOE/MOA. Template BTP pour architectes.',
      keywords: [
        'gestion chantier architecte',
        'suivi maîtrise d\'oeuvre notion',
        'compte-rendu réunion chantier notion',
        'template notion architecte',
        'coordination moe moa notion'
      ],
      openGraph: {
        title: 'Gestion de chantier architecte',
        description:
          'Centralisez réunions, comptes-rendus et suivi réserves avec un template Notion pour architectes MOE.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un espace Notion dédié à votre maîtrise d\'œuvre',
      description: 'Architecture résidentielle, tertiaire ou ERP : nous créons un template MOE adapté à votre pratique et vos types de chantiers.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Architecte MOE'
    },
    category: 'metier'
  },
  // ── Pages cœur chantier ──────────────────────────────────
  {
    id: 'suivi-chantier-batiment',
    slug: 'suivi-chantier-batiment',
    path: '/suivi-chantier-batiment',
    title: 'Suivi de chantier bâtiment',
    subtitle:
      'Centralisez l\'avancement, les intervenants et les documents de vos chantiers BTP dans Notion.',
    description:
      'Un suivi de chantier efficace repose sur une information centralisée et accessible à tous. Notion remplace les tableurs et les carnets de chantier par un espace structuré.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'L\'information de chantier circule mal : entre les mails, les SMS et les appels téléphoniques, personne n\'a la même version à jour.',
      bullets: [
        'Avancement du chantier connu seulement de celui qui est sur place',
        'Documents éparpillés entre email, papier et dossier partagé',
        'Retards non anticipés faute de visibilité sur les dépendances'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est la solution',
      description:
        'Notion crée un espace de suivi unique, accessible depuis le bureau et le terrain, où chaque chantier a sa fiche, son planning et ses documents.',
      bullets: [
        'Vue d\'ensemble de tous les chantiers en cours',
        'Avancement en temps réel mis à jour depuis le terrain',
        'Documents centralisés et retrouvables en 10 secondes'
      ]
    },
    longForm: {
      intro: [
        "Le suivi de chantier est l'une des tâches les plus critiques dans le BTP. Un chantier mal suivi accumule les retards, les mauvaises surprises et les litiges. Pourtant, la plupart des professionnels s'appuient encore sur des outils inadaptés : tableurs, carnets papier, fils de SMS.",
        "Notion change la donne en offrant un espace de suivi structuré, accessible depuis n'importe quel appareil et mis à jour collaborativement. Voici comment en tirer le meilleur parti."
      ],
      sections: [
        {
          title: 'Créer une fiche de suivi par chantier',
          paragraphs: [
            "Chaque chantier mérite une fiche dédiée : maître d'ouvrage, adresse, entreprises intervenantes, planning prévisionnel, budget et statut d'avancement.",
            "Toute l'équipe travaille depuis la même fiche, ce qui élimine les versions multiples et les informations contradictoires."
          ],
          bullets: [
            'Statuts clairs : devis, démarrage, en cours, réception, SAV',
            'Contacts clients et entreprises attachés au chantier',
            'Budget prévisionnel vs réalisé en un coup d\'œil'
          ]
        },
        {
          title: 'Suivre l\'avancement en temps réel',
          paragraphs: [
            "Le suivi d'avancement ne doit pas dépendre d'un appel téléphonique. Notion permet aux chefs de chantier de mettre à jour l'avancement directement depuis leur smartphone.",
            "Le conducteur de travaux et le client voient l'état du chantier en temps réel, sans solliciter personne."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template BTP est conçu pour le suivi de chantier quotidien : fiches chantier structurées, comptes-rendus de réunion, gestion des intervenants et suivi des commissions. Il couvre l'ensemble du cycle de vie d'un chantier.",
            "Déjà utilisé par plus de 150 entreprises du BTP en France, il s'adapte aux chantiers de toutes tailles, du dépannage d'urgence au programme immobilier."
          ],
          bullets: [
            'Tableau de bord de tous les chantiers en cours',
            'Fiches chantier complètes avec planning et intervenants',
            'Comptes-rendus de réunion structurés et archivés',
            'Suivi des commissions et des décisions',
            'Documents, devis et photos par chantier'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'pilotage-copropriete-chantiers'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/organiser-chantier-renovation',
      '/planning-chantier-batiment',
      '/suivi-travaux-maison',
      '/outil-gestion-chantier-batiment'
    ],
    seo: {
      title: 'Suivi de Chantier Bâtiment | Template Notion BTP',
      description:
        'Suivez vos chantiers BTP avec Notion : avancement en temps réel, intervenants, documents et comptes-rendus. Template de suivi de chantier bâtiment.',
      keywords: [
        'suivi de chantier bâtiment',
        'suivi chantier btp notion',
        'template suivi chantier',
        'avancement chantier bâtiment',
        'outil suivi chantier btp'
      ],
      openGraph: {
        title: 'Suivi de chantier bâtiment',
        description: 'Centralisez le suivi de tous vos chantiers BTP dans un espace Notion structuré.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un template de suivi de chantier adapté à votre activité',
      description: 'Artisan, PME du bâtiment ou conducteur de travaux : nous configurons votre espace de suivi Notion selon votre organisation.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Suivi de chantier BTP'
    },
    category: 'metier'
  },
  {
    id: 'outil-gestion-chantier-batiment',
    slug: 'outil-gestion-chantier-batiment',
    path: '/outil-gestion-chantier-batiment',
    title: 'Outil de gestion de chantier bâtiment',
    subtitle:
      'Un outil simple, puissant et accessible partout pour piloter vos chantiers BTP au quotidien.',
    description:
      'Les professionnels du BTP cherchent un outil de gestion de chantier qui fonctionne réellement sur le terrain. Notion, avec le bon template, remplace une dizaine d\'outils fragmentés.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'La plupart des outils de gestion de chantier sont trop complexes, trop chers ou inadaptés à la réalité d\'un artisan ou d\'une PME du bâtiment.',
      bullets: [
        'Logiciels métier BTP coûteux et difficiles à prendre en main',
        'Excel puissant mais non collaboratif et source d\'erreurs',
        'Applications mobiles limitées qui ne parlent pas au bureau'
      ]
    },
    notion: {
      title: 'Notion comme outil de gestion de chantier',
      description:
        'Notion combine la puissance d\'une base de données, la flexibilité d\'un tableur et l\'accessibilité d\'une application mobile. Le tout pour un prix raisonnable.',
      bullets: [
        'Accessible sur ordinateur, tablette et smartphone',
        'Mis à jour en temps réel par toute l\'équipe',
        'Personnalisable selon votre façon de travailler'
      ]
    },
    longForm: {
      intro: [
        "Le bon outil de gestion de chantier, c'est celui que votre équipe utilise vraiment. Pas le plus sophistiqué, ni le plus cher : celui qui s'adapte à votre façon de travailler et qui donne de l'information utile en quelques secondes.",
        "Notion s'est imposé comme l'outil de référence pour de nombreuses PME du BTP et artisans indépendants. Sa flexibilité permet de créer exactement le système dont vous avez besoin, sans développement informatique."
      ],
      sections: [
        {
          title: 'Pourquoi Notion est adapté au BTP',
          paragraphs: [
            "Notion fonctionne comme une base de données reliée : un chantier est connecté à ses contacts, ses documents, ses réunions et ses tâches. Vous visualisez tout le contexte d'un projet sans navigation complexe.",
            "Contrairement aux logiciels BTP spécialisés, Notion s'adapte à chaque métier : électricien, maçon, couvreur ou conducteur de travaux peuvent configurer leur espace différemment."
          ],
          bullets: [
            'Base de données relationnelle sans code',
            'Vues personnalisées : Kanban, calendrier, tableau, liste',
            'Partage avec clients et partenaires possible'
          ]
        },
        {
          title: 'Ce qu\'un bon outil de chantier doit faire',
          paragraphs: [
            "Un outil de gestion de chantier efficace doit permettre de suivre l'avancement, gérer les documents, coordonner les équipes et communiquer avec le client. Sans multiplier les applications.",
            "Notion regroupe toutes ces fonctions dans un espace unique, accessible hors ligne depuis le terrain."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template est l'outil de gestion de chantier prêt à l'emploi que des centaines de professionnels du BTP attendaient. Il structure chantiers, réunions, commissions et documents dans Notion, sans configuration complexe.",
            "En quelques minutes, vous avez un espace de pilotage complet pour tous vos chantiers. Utilisé par plus de 150 entreprises du bâtiment en France."
          ],
          bullets: [
            'Tableau de bord multi-chantiers',
            'Fiches chantier complètes prêtes à remplir',
            'Réunions de chantier et comptes-rendus intégrés',
            'Gestion des commissions et intervenants',
            'Import possible de vos chantiers existants'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'gestion-projet-notion-guide-complet'],
    relatedPillarPaths: [
      '/suivi-chantier-batiment',
      '/planning-chantier-batiment',
      '/gestion-chantier-artisan',
      '/outil-gestion-artisan-independant',
      '/gestion-projets-artisan'
    ],
    seo: {
      title: 'Outil Gestion Chantier Bâtiment | Template Notion BTP',
      description:
        'Découvrez l\'outil de gestion de chantier BTP le plus simple : template Notion pour artisans et PME. Suivi chantiers, réunions, devis et documents.',
      keywords: [
        'outil gestion chantier bâtiment',
        'logiciel gestion chantier btp',
        'outil suivi chantier artisan',
        'template notion gestion chantier',
        'application gestion chantier bâtiment'
      ],
      openGraph: {
        title: 'Outil de gestion de chantier bâtiment',
        description: 'Le template Notion qui remplace votre logiciel BTP : simple, efficace et accessible depuis le terrain.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Testez le meilleur outil de gestion de chantier pour votre métier',
      description: 'Nous adaptons le template à votre activité BTP : corps de métier, taille d\'équipe et type de chantiers.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Outil gestion chantier BTP'
    },
    category: 'metier'
  },
  {
    id: 'planning-chantier-batiment',
    slug: 'planning-chantier-batiment',
    path: '/planning-chantier-batiment',
    title: 'Planning chantier bâtiment',
    subtitle:
      'Planifiez vos chantiers BTP avec précision : jalons, corps de métier, délais et ressources dans Notion.',
    description:
      'Un planning de chantier qui tient, c\'est la base d\'un chantier qui se passe bien. Notion structure vos plannings BTP et alerte sur les retards avant qu\'ils dégénèrent.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Les plannings de chantier sont souvent faits une fois au départ puis jamais mis à jour. Résultat : on découvre les retards trop tard pour les absorber.',
      bullets: [
        'Planning initial déconnecté de la réalité terrain dès la 2ème semaine',
        'Aucune vision des dépendances entre corps de métier',
        'Retards en cascade non anticipés'
      ]
    },
    notion: {
      title: 'Pourquoi Notion structure le planning',
      description:
        'Notion permet de créer un planning de chantier vivant, mis à jour par l\'équipe terrain et visible par toutes les parties prenantes.',
      bullets: [
        'Planning visuel par phase et par corps de métier',
        'Dépendances entre tâches pour anticiper les blocages',
        'Alertes sur les jalons critiques dépassés'
      ]
    },
    longForm: {
      intro: [
        "Un bon planning de chantier BTP doit être réaliste, partagé et mis à jour régulièrement. La plupart des dépassements de délais viennent non pas d'imprévus mais d'un planning mal suivi et jamais actualisé.",
        "Notion permet de créer un planning de chantier collaboratif, visible par tous les intervenants et mis à jour en temps réel depuis le terrain. Voici comment structurer le planning de vos chantiers bâtiment."
      ],
      sections: [
        {
          title: 'Structurer le planning par phase',
          paragraphs: [
            "Chaque chantier BTP se décompose en phases distinctes : préparation, gros œuvre, second œuvre, finitions, réception. Notion structure ces phases avec des dates de début, des durées et des responsables.",
            "Vous visualisez immédiatement quel lot est en retard et quel impact cela a sur les phases suivantes."
          ],
          bullets: [
            'Phases de chantier avec dates planifiées et réelles',
            'Responsable assigné à chaque phase',
            'Durée estimée vs durée réelle'
          ]
        },
        {
          title: 'Coordonner les intervenants dans le temps',
          paragraphs: [
            "Plusieurs corps de métier interviennent sur un même chantier à des moments précis. Notion crée un planning d'intervention par artisan, avec les contraintes de succession.",
            "Chaque intervenant connaît sa fenêtre d'intervention à l'avance et peut s'organiser. Les chevauchements ou les trous sont visibles et corrigés en amont."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template intègre un module de planning de chantier structuré, relié aux fiches chantier, aux comptes-rendus de réunion et aux intervenants. C'est le seul outil dont vous avez besoin pour piloter un chantier BTP.",
            "Le planning est visible par toute l'équipe et mis à jour collaborativement. Plus de tableur qui traîne sur un seul ordinateur."
          ],
          bullets: [
            'Planning de chantier par phase avec jalons',
            'Vue calendrier des interventions',
            'Alertes sur les retards et dépendances',
            'Lien direct entre planning et comptes-rendus',
            'Historique des révisions du planning'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'gestion-projet-notion-guide-complet'],
    relatedPillarPaths: [
      '/suivi-chantier-batiment',
      '/outil-gestion-chantier-batiment',
      '/organiser-chantier-renovation',
      '/planning-travaux-renovation',
      '/gestion-chantier-architecte'
    ],
    seo: {
      title: 'Planning Chantier Bâtiment | Template Notion BTP',
      description:
        'Créez et suivez votre planning chantier BTP avec Notion : phases, corps de métier, jalons et alertes retard. Template planning chantier bâtiment.',
      keywords: [
        'planning chantier bâtiment',
        'planning btp notion',
        'planning travaux bâtiment',
        'planning chantier construction',
        'template planning chantier notion'
      ],
      openGraph: {
        title: 'Planning chantier bâtiment',
        description: 'Structurez et suivez votre planning chantier BTP avec un template Notion adapté.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un planning de chantier Notion qui tient dans le temps',
      description: 'Nous créons un template de planning adapté à vos types de chantiers et votre façon de travailler avec les intervenants.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Planning chantier BTP'
    },
    category: 'metier'
  },
  {
    id: 'suivi-travaux-maison',
    slug: 'suivi-travaux-maison',
    path: '/suivi-travaux-maison',
    title: 'Suivi travaux maison',
    subtitle:
      'Suivez l\'avancement de vos travaux, coordonnez les artisans et maîtrisez votre budget rénovation.',
    description:
      'Que vous soyez maître d\'ouvrage ou conducteur de travaux, Notion vous donne une vue claire sur vos travaux maison : avancement, budget, artisans et documents.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Suivre des travaux chez soi ou pour un client est stressant quand l\'information n\'est pas centralisée : on perd le fil des artisans, des devis et des avancements.',
      bullets: [
        'Devis de plusieurs artisans difficiles à comparer et retrouver',
        'Planning des interventions géré par SMS et coups de fil',
        'Budget réel vs prévu jamais réconcilié en cours de chantier'
      ]
    },
    notion: {
      title: 'Pourquoi Notion simplifie le suivi des travaux',
      description:
        'Notion centralise devis, planning, avancement et budget dans un espace unique, partageable avec les artisans et le client.',
      bullets: [
        'Comparatif des devis par lot de travaux',
        'Planning des interventions par artisan',
        'Suivi budgétaire en temps réel'
      ]
    },
    longForm: {
      intro: [
        "Faire des travaux chez soi ou suivre un chantier pour un client, c'est gérer une multitude d'informations en même temps : devis, plannings, factures, photos, garanties. Sans système, on perd vite le fil et les dépassements de budget arrivent sans prévenir.",
        "Notion est l'outil idéal pour centraliser tout le suivi de travaux maison dans un espace simple, accessible depuis n'importe quel appareil."
      ],
      sections: [
        {
          title: 'Centraliser les devis et artisans',
          paragraphs: [
            "Avant de démarrer les travaux, vous avez souvent plusieurs devis par lot. Notion crée un comparatif structuré : artisan, montant, délai, références. Vous décidez sur des critères clairs.",
            "Une fois l'artisan retenu, son devis, ses coordonnées et ses disponibilités sont attachés à la fiche travaux. Tout est retrouvable en 10 secondes."
          ],
          bullets: [
            'Comparatif devis par lot de travaux',
            'Fiches artisans avec contacts et documents',
            'Suivi des validations et signatures'
          ]
        },
        {
          title: 'Suivre l\'avancement et les visites de chantier',
          paragraphs: [
            "Chaque visite de chantier mérite un compte-rendu photo : avant, pendant, après. Notion archive ces photos et les relie aux lots de travaux correspondants.",
            "Vous avez une preuve de l'avancement et une protection en cas de désaccord avec un artisan sur ce qui a été réalisé."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template est aussi bien utilisé par des particuliers qui suivent leur rénovation que par des conducteurs de travaux professionnels. Il structure le suivi des travaux maison de façon claire et accessible.",
            "Devis, factures, comptes-rendus, photos et planning sont tous reliés dans un seul espace. Vous avez enfin une vision complète de votre chantier."
          ],
          bullets: [
            'Suivi des lots de travaux avec avancement',
            'Budget : devis acceptés, factures, reste à régler',
            'Photos de chantier classées par lot',
            'Comptes-rendus de visite avec liste de points',
            'Documents de fin de chantier : garanties, DOE'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'pilotage-copropriete-chantiers'],
    relatedPillarPaths: [
      '/organiser-chantier-renovation',
      '/organisation-renovation-maison',
      '/planning-travaux-renovation',
      '/suivi-budget-chantier',
      '/gestion-devis-chantier'
    ],
    seo: {
      title: 'Suivi Travaux Maison | Template Notion BTP',
      description:
        'Suivez vos travaux maison avec Notion : planning artisans, suivi budget, comptes-rendus et photos. Template de suivi de travaux pour particuliers et pros.',
      keywords: [
        'suivi travaux maison',
        'suivi chantier maison notion',
        'suivre travaux rénovation',
        'template suivi travaux',
        'planifier travaux maison notion'
      ],
      openGraph: {
        title: 'Suivi travaux maison',
        description: 'Centralisez tout le suivi de vos travaux maison : artisans, budget, planning et photos.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un espace Notion pour suivre vos travaux maison',
      description: 'Rénovation complète, extension ou second œuvre : nous créons un template adapté à l\'ampleur de vos travaux.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Suivi travaux maison'
    },
    category: 'metier'
  },
  // ── Pages par métier ─────────────────────────────────────
  {
    id: 'suivi-chantier-menuisier',
    slug: 'suivi-chantier-menuisier',
    path: '/suivi-chantier-menuisier',
    title: 'Suivi de chantier menuisier',
    subtitle:
      'Gérez vos poses, vos commandes et vos clients avec un système de suivi adapté à la menuiserie.',
    description:
      'Un menuisier qui intervient en pose sur chantier jongle entre commandes de matériaux, délais de livraison et planning des poses. Notion structure tout ça simplement.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'La menuiserie a une contrainte spécifique : les délais de fabrication conditionnent tout le reste. Un retard de livraison bloque la pose et retarde tout le chantier.',
      bullets: [
        'Délais de livraison matériaux non suivis systématiquement',
        'Poses planifiées avant confirmation de la livraison',
        'SAV et garanties difficiles à gérer sans historique des poses'
      ]
    },
    notion: {
      title: 'Pourquoi Notion convient à la menuiserie',
      description:
        'Notion relie les commandes aux poses, les poses aux chantiers et les chantiers aux clients. Vous ne ratez plus aucune livraison et planifiez les poses avec certitude.',
      bullets: [
        'Suivi des commandes et délais fournisseurs',
        'Planning de pose lié aux confirmations de livraison',
        'Historique des poses avec photos et PV de réception'
      ]
    },
    longForm: {
      intro: [
        "Le menuisier a une double activité : la fabrication ou la commande des éléments, et la pose sur chantier. Ces deux phases doivent être coordonnées précisément : une pose planifiée sans livraison confirmée génère un déplacement inutile et un client mécontent.",
        "Notion permet de créer un système de suivi qui couvre les deux phases et leur synchronisation, accessible depuis l'atelier comme depuis le chantier."
      ],
      sections: [
        {
          title: 'Suivre les commandes et livraisons',
          paragraphs: [
            "Chaque chantier génère des commandes chez les fournisseurs : fenêtres, portes, parquet, escaliers. Notion centralise ces commandes avec les délais annoncés et les dates de livraison confirmées.",
            "Vous planifiez les poses uniquement quand la livraison est confirmée. Plus de déplacements à vide."
          ],
          bullets: [
            'Commandes fournisseurs avec délais et suivi',
            'Alerte automatique à la date de livraison attendue',
            'Lien commande → pose → chantier'
          ]
        },
        {
          title: 'Planifier les poses par chantier',
          paragraphs: [
            "Les poses de menuiserie s'insèrent dans un planning de chantier global. Notion intègre vos créneaux de pose dans une vue calendrier partagée avec le conducteur de travaux.",
            "Vous optimisez vos déplacements en regroupant les poses géographiquement et temporellement."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template BTP couvre tout le cycle de vie d'un chantier menuiserie : de la commande fournisseur au PV de réception, en passant par la pose et les éventuels SAV.",
            "Il est utilisé par des menuisiers indépendants et des PME de menuiserie pour professionnaliser leur suivi et réduire les litiges clients."
          ],
          bullets: [
            'Suivi des commandes et livraisons par chantier',
            'Planning de pose avec confirmations de livraison',
            'PV de réception et photos de pose',
            'Historique SAV et garanties',
            'Devis et factures liés aux chantiers'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/suivi-chantier-batiment',
      '/planning-chantier-batiment',
      '/gestion-chantier-couvreur',
      '/planning-chantier-peintre'
    ],
    seo: {
      title: 'Suivi Chantier Menuisier | Template Notion BTP',
      description:
        'Gérez vos chantiers de menuiserie avec Notion : commandes, délais, planning de pose et SAV. Template de suivi chantier pour menuisiers.',
      keywords: [
        'suivi chantier menuisier',
        'gestion chantier menuiserie notion',
        'planning pose menuiserie',
        'template notion menuisier',
        'suivi commandes menuiserie'
      ],
      openGraph: {
        title: 'Suivi de chantier menuisier',
        description: 'Coordonnez commandes, livraisons et poses menuiserie avec un template Notion.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion pour votre activité de menuiserie',
      description: 'Menuiserie intérieure, extérieure ou agenceur : nous adaptons le template à votre type de chantiers.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Menuisier BTP'
    },
    category: 'metier'
  },
  {
    id: 'planning-chantier-peintre',
    slug: 'planning-chantier-peintre',
    path: '/planning-chantier-peintre',
    title: 'Planning chantier peintre',
    subtitle:
      'Planifiez vos chantiers de peinture, gérez vos équipes et suivez vos clients depuis Notion.',
    description:
      'Un peintre en bâtiment intervient souvent sur plusieurs chantiers en parallèle avec une ou plusieurs équipes. Un bon planning est la clé d\'une activité fluide.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'La peinture intervient en fin de chantier, quand tout le monde est pressé. Un retard d\'une autre entreprise décale tout, et le peintre doit s\'adapter en permanence.',
      bullets: [
        'Planning constamment bousculé par les retards des autres corps de métier',
        'Gestion des équipes de peintres complexe sur plusieurs chantiers simultanés',
        'Devis de quantité difficiles à établir et suivre'
      ]
    },
    notion: {
      title: 'Pourquoi Notion aide les peintres',
      description:
        'Notion crée un planning flexible, mis à jour en temps réel, avec une vue par équipe et par chantier qui s\'adapte aux aléas du terrain.',
      bullets: [
        'Planning par équipe et par chantier mis à jour facilement',
        'Gestion des matériaux et teintes par chantier',
        'Comptes-rendus d\'intervention et photos avant/après'
      ]
    },
    longForm: {
      intro: [
        "La peinture en bâtiment a une spécificité : elle intervient en toute fin de chantier, quand toutes les autres entreprises ont fini. Cela en fait l'un des corps de métier les plus exposés aux retards des autres. Un bon planning doit être flexible et facile à mettre à jour.",
        "Notion permet de créer un planning de chantier réactif, visible par toutes les équipes et adaptable en quelques clics quand la situation change."
      ],
      sections: [
        {
          title: 'Gérer le planning par équipe',
          paragraphs: [
            "Avec plusieurs équipes de peintres sur différents chantiers, la coordination est complexe. Notion crée une vue calendrier par équipe : qui est où, quand et pour combien de jours.",
            "En cas de décalage sur un chantier, vous réaffectez une équipe en quelques clics sans perdre de vue l'ensemble."
          ],
          bullets: [
            'Vue calendrier par équipe de peinture',
            'Suivi des heures par chantier et par équipe',
            'Alertes sur les chevauchements de planning'
          ]
        },
        {
          title: 'Suivre les matériaux et teintes',
          paragraphs: [
            "Chaque chantier a ses teintes, ses quantités et ses finitions. Notion centralise ces informations par chantier, avec les références fabricant et les quantités commandées.",
            "En cas de reprise ou de SAV, vous retrouvez immédiatement la teinte et le fournisseur pour une réparation invisible."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template BTP s'adapte à l'activité d'un peintre : planning des équipes, suivi des chantiers en cours, gestion des matériaux et comptes-rendus de visite. Un espace unique pour piloter toute l'activité.",
            "Des peintres indépendants aux PME de 10 peintres, il s'adapte à la taille de votre structure."
          ],
          bullets: [
            'Planning multi-équipes par chantier',
            'Suivi des teintes et matériaux par chantier',
            'Photos avant/après attachées aux interventions',
            'Devis quantitatifs et suivi facturation',
            'Historique SAV et retouches'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/suivi-chantier-menuisier',
      '/gestion-chantier-couvreur',
      '/planning-chantier-batiment',
      '/organiser-chantier-renovation'
    ],
    seo: {
      title: 'Planning Chantier Peintre | Template Notion BTP',
      description:
        'Planifiez vos chantiers de peinture avec Notion : gestion des équipes, matériaux, teintes et suivi clients. Template planning chantier peintre.',
      keywords: [
        'planning chantier peintre',
        'gestion chantier peinture notion',
        'planning équipes peinture bâtiment',
        'template notion peintre',
        'suivi chantier peinture'
      ],
      openGraph: {
        title: 'Planning chantier peintre',
        description: 'Gérez vos équipes et chantiers de peinture avec un template Notion flexible.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion pour votre activité de peinture',
      description: 'Peinture intérieure, ravalement ou décoration : nous créons un espace adapté à vos équipes et vos types de chantiers.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Peintre BTP'
    },
    category: 'metier'
  },
  {
    id: 'gestion-chantier-couvreur',
    slug: 'gestion-chantier-couvreur',
    path: '/gestion-chantier-couvreur',
    title: 'Gestion de chantier couvreur',
    subtitle:
      'Planifiez vos chantiers de couverture, suivez les interventions urgentes et gérez votre stock de matériaux.',
    description:
      'Un couvreur gère des chantiers planifiés et des urgences (fuites, tempêtes). Notion centralise les deux avec un suivi rigoureux et un historique client complet.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'La couverture mêle chantiers planifiés et urgences météo imprévisibles. Sans organisation, les urgences perturbent les chantiers en cours et les clients sont mal informés.',
      bullets: [
        'Urgences toiture qui désorganisent le planning hebdomadaire',
        'Suivi des garanties décennales complexe sans historique des interventions',
        'Commandes matériaux (tuiles, ardoises, zinc) gérées à vue'
      ]
    },
    notion: {
      title: 'Pourquoi Notion convient à la couverture',
      description:
        'Notion sépare les urgences des chantiers planifiés, centralise l\'historique des toitures par client et suit les commandes de matériaux.',
      bullets: [
        'File d\'urgences toiture séparée des chantiers planifiés',
        'Historique complet par adresse : interventions, matériaux, garanties',
        'Commandes matériaux liées aux chantiers'
      ]
    },
    longForm: {
      intro: [
        "Un couvreur a une double exposition : des chantiers longs planifiés à l'avance (réfection de toiture, charpente) et des urgences qui arrivent sans prévenir après une tempête ou une fuite. Gérer les deux en même temps demande une organisation sans faille.",
        "Notion permet de créer deux flux distincts qui se complètent : le planning des chantiers et la gestion des urgences. L'historique de chaque toiture est centralisé pour un suivi client irréprochable."
      ],
      sections: [
        {
          title: 'Gérer les urgences toiture',
          paragraphs: [
            "Après un épisode de vent fort, les demandes d'urgence affluent. Notion crée une file de demandes triées par priorité, secteur géographique et type d'intervention.",
            "Vous répondez aux urgences sans perdre de vue vos chantiers planifiés, et chaque intervention urgente est documentée pour la facturation et l'assurance."
          ],
          bullets: [
            'File d\'urgences avec priorité et localisation',
            'Fiche intervention rapide pour les dépannages',
            'Rapport d\'intervention avec photos pour assurances'
          ]
        },
        {
          title: 'Piloter les chantiers de couverture',
          paragraphs: [
            "Une réfection de toiture complète est un chantier complexe : dépose, isolation, charpente, couverture, zinguerie. Notion structure ces phases et suit l'avancement.",
            "Les conditions météo impactent le planning : Notion permet de replanifier rapidement sans perdre le fil."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template BTP gère les deux aspects de l'activité couverture : urgences et chantiers planifiés. Chaque toiture a son historique complet, accessible en quelques secondes pour un SAV ou une garantie décennale.",
            "Il est utilisé par des couvreurs indépendants et des PME de couverture-zinguerie pour professionnaliser leur suivi et réduire les litiges."
          ],
          bullets: [
            'File d\'urgences et chantiers planifiés séparés',
            'Historique par adresse : interventions, matériaux, garanties',
            'Suivi commandes tuiles, ardoises, zinc, membranes',
            'Rapports d\'intervention pour assurances',
            'Planifier les chantiers sous contrainte météo'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/suivi-chantier-menuisier',
      '/planning-chantier-peintre',
      '/organiser-chantier-renovation',
      '/suivi-chantier-batiment'
    ],
    seo: {
      title: 'Gestion Chantier Couvreur | Template Notion BTP',
      description:
        'Gérez vos chantiers de couverture avec Notion : urgences toiture, chantiers planifiés, matériaux et garanties. Template BTP pour couvreurs.',
      keywords: [
        'gestion chantier couvreur',
        'suivi chantier couverture notion',
        'planning chantier couvreur',
        'template notion couvreur',
        'urgence toiture suivi'
      ],
      openGraph: {
        title: 'Gestion de chantier couvreur',
        description: 'Gérez urgences toiture et chantiers planifiés avec un template Notion pour couvreurs.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion pour votre activité de couverture',
      description: 'Couverture tuile, ardoise, zinc ou étanchéité : nous créons un template adapté à vos types de chantiers.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Couvreur BTP'
    },
    category: 'metier'
  },
  // ── Pages pour indépendants ──────────────────────────────
  {
    id: 'organisation-entreprise-batiment',
    slug: 'organisation-entreprise-batiment',
    path: '/organisation-entreprise-batiment',
    title: 'Organisation d\'une entreprise du bâtiment',
    subtitle:
      'Structurez vos chantiers, votre équipe et votre administratif dans un système d\'organisation BTP efficace.',
    description:
      'Une entreprise du bâtiment performante repose sur une organisation solide : chantiers, équipes, devis, facturation. Notion centralise tout dans un système cohérent.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Les PME du bâtiment grandissent vite mais leur organisation reste souvent artisanale : chaque chef de chantier a son système, l\'information ne circule pas.',
      bullets: [
        'Chaque chef de chantier gère ses infos dans son coin',
        'Le dirigeant n\'a pas de vue d\'ensemble sur l\'activité',
        'L\'administratif (devis, factures, RH) n\'est pas relié aux chantiers'
      ]
    },
    notion: {
      title: 'Pourquoi Notion structure les PME BTP',
      description:
        'Notion centralise chantiers, équipes et administratif dans un espace partagé. Le dirigeant a une vue d\'ensemble, les chefs de chantier ont leur espace de travail.',
      bullets: [
        'Vue d\'ensemble du dirigeant : chantiers, CA, équipes',
        'Espaces de travail par chef de chantier',
        'Lien chantiers ↔ devis ↔ factures ↔ RH'
      ]
    },
    longForm: {
      intro: [
        "Passer d'artisan indépendant à entrepreneur du bâtiment avec des salariés est un saut organisationnel majeur. Les informations qui tenaient dans une tête doivent maintenant circuler entre plusieurs personnes. Sans système, le dirigeant devient le goulot d'étranglement de toutes les décisions.",
        "Notion permet de créer une organisation claire pour une PME du bâtiment : chaque chantier a son espace, chaque équipe ses outils, et le dirigeant a sa vue d'ensemble."
      ],
      sections: [
        {
          title: 'Piloter l\'activité commerciale',
          paragraphs: [
            "Le carnet de commandes est la colonne vertébrale d'une entreprise BTP. Notion centralise les prospects, les devis en cours et les chantiers signés dans un pipeline commercial clair.",
            "Le dirigeant voit immédiatement le chiffre d'affaires prévu, les devis à relancer et les chantiers à démarrer. Plus de surprises sur la charge de travail."
          ],
          bullets: [
            'Pipeline commercial : prospects, devis, signés',
            'CA prévisionnel par mois et par chantier',
            'Taux de transformation des devis'
          ]
        },
        {
          title: 'Coordonner les équipes et chantiers',
          paragraphs: [
            "Chaque chef de chantier a son espace dans Notion : ses chantiers en cours, son planning, ses comptes-rendus. Le dirigeant accède à tous les espaces pour avoir une vision globale.",
            "Les informations remontent naturellement sans réunion quotidienne : l'avancement est visible en temps réel."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template BTP est la base d'organisation pour les entreprises du bâtiment qui veulent structurer leur activité : suivi des chantiers, gestion des équipes, comptes-rendus de réunion et suivi des commissions.",
            "Il évolue avec vous : commencez avec les fonctions essentielles et activez les modules avancés au fur et à mesure de votre croissance."
          ],
          bullets: [
            'Tableau de bord dirigeant avec vue d\'ensemble',
            'Espaces chantier pour chaque chef de chantier',
            'Pipeline commercial et suivi des devis',
            'Gestion des sous-traitants et fournisseurs',
            'Suivi facturation et trésorerie chantiers'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'gestion-projet-notion-guide-complet'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/outil-gestion-artisan-independant',
      '/gestion-projets-artisan',
      '/suivi-artisans-chantier',
      '/suivi-budget-chantier'
    ],
    seo: {
      title: 'Organisation Entreprise Bâtiment | Template Notion BTP',
      description:
        'Organisez votre entreprise du bâtiment avec Notion : chantiers, équipes, devis et facturation. Template BTP pour PME du bâtiment.',
      keywords: [
        'organisation entreprise bâtiment',
        'gestion pme bâtiment notion',
        'organiser entreprise btp',
        'template notion entreprise bâtiment',
        'pilotage activité bâtiment notion'
      ],
      openGraph: {
        title: 'Organisation d\'une entreprise du bâtiment',
        description: 'Structurez votre PME du bâtiment avec un template Notion : chantiers, équipes et administratif.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un système d\'organisation Notion pour votre entreprise BTP',
      description: 'De 2 à 50 salariés : nous créons un espace Notion adapté à votre structure et votre activité.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Entreprise du bâtiment'
    },
    category: 'metier'
  },
  {
    id: 'outil-gestion-artisan-independant',
    slug: 'outil-gestion-artisan-independant',
    path: '/outil-gestion-artisan-independant',
    title: 'Outil de gestion pour artisan indépendant',
    subtitle:
      'Un système simple pour gérer seul vos chantiers, clients, devis et facturation sans vous noyer dans l\'administratif.',
    description:
      'Un artisan indépendant est à la fois technicien, commercial et gestionnaire. Notion lui donne les outils d\'un système professionnel sans la complexité des logiciels métier.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'L\'artisan indépendant travaille seul et n\'a pas le temps de gérer son administratif correctement. Les devis traînent, les relances sont oubliées et la trésorerie est floue.',
      bullets: [
        'Devis envoyés mais jamais relancés = CA perdu',
        'Facturation faite en retard car pas de suivi',
        'Aucune visibilité sur la trésorerie à 3 mois'
      ]
    },
    notion: {
      title: 'Pourquoi Notion est l\'outil idéal pour l\'artisan solo',
      description:
        'Notion centralise tout dans un seul espace : chantiers, clients, devis, factures. 15 minutes par semaine suffisent pour tout tenir à jour.',
      bullets: [
        'Tableau de bord personnalisé en 5 minutes',
        'Relances automatiques sur les devis en attente',
        'Vue trésorerie simple et lisible'
      ]
    },
    longForm: {
      intro: [
        "Un artisan indépendant porte plusieurs casquettes en même temps : il réalise le travail, gère la relation client, envoie les devis, relance les impayés et achète les matériaux. Sans système, l'administratif s'accumule et finit par déborder sur les nuits et les week-ends.",
        "Notion permet à l'artisan solo de se créer un système de gestion complet en quelques heures, sans formation technique et sans abonnement coûteux."
      ],
      sections: [
        {
          title: 'Gérer les clients et les chantiers',
          paragraphs: [
            "Une fiche client simple avec son historique de chantiers, ses coordonnées et ses documents suffit à transformer la relation client. Vous retrouvez l'historique d'un client en 10 secondes.",
            "Les chantiers sont reliés aux clients, aux devis et aux factures. Tout est cohérent et retrouvable."
          ],
          bullets: [
            'Fiche client avec historique des travaux réalisés',
            'Chantiers liés au client, au devis et à la facture',
            'Contacts et documents centralisés par client'
          ]
        },
        {
          title: 'Suivre les devis et la facturation',
          paragraphs: [
            "Un devis non relancé, c'est souvent un chantier perdu. Notion crée un pipeline commercial simple : devis envoyé, relance prévue, signé, facturé, payé.",
            "Vous visualisez votre carnet de commandes et anticipez les périodes creuses pour prospecter au bon moment."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template est conçu pour les artisans qui veulent un système professionnel sans la complexité d'un logiciel métier. Il couvre la gestion des chantiers, des clients, des réunions et des commissions dans Notion.",
            "Simple à prendre en main, il remplace efficacement les tableurs et les carnets papier que la plupart des artisans utilisent encore."
          ],
          bullets: [
            'Fiches client et historique des chantiers',
            'Pipeline devis avec statuts et relances',
            'Suivi des chantiers en cours',
            'Comptes-rendus d\'intervention',
            'Vue trésorerie : encaissements prévus et reçus'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/gestion-projets-artisan',
      '/organisation-entreprise-batiment',
      '/gestion-devis-chantier',
      '/suivi-budget-chantier'
    ],
    seo: {
      title: 'Outil Gestion Artisan Indépendant | Template Notion BTP',
      description:
        'Gérez votre activité d\'artisan indépendant avec Notion : chantiers, clients, devis et facturation. L\'outil de gestion simple pour artisans.',
      keywords: [
        'outil gestion artisan indépendant',
        'gestion artisan seul notion',
        'logiciel artisan indépendant simple',
        'template notion artisan indépendant',
        'gérer activité artisan btp'
      ],
      openGraph: {
        title: 'Outil de gestion pour artisan indépendant',
        description: 'Le système de gestion simple pour artisans indépendants : chantiers, devis, clients dans Notion.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un système Notion taillé pour votre activité en solo',
      description: 'Auto-entrepreneur ou artisan indépendant : nous créons un espace simple et efficace pour gérer votre activité.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Artisan indépendant'
    },
    category: 'metier'
  },
  {
    id: 'gestion-projets-artisan',
    slug: 'gestion-projets-artisan',
    path: '/gestion-projets-artisan',
    title: 'Gestion de projets artisan',
    subtitle:
      'Pilotez vos projets de A à Z : de la prise de contact client à la réception des travaux.',
    description:
      'Pour un artisan, chaque chantier est un projet avec ses étapes, ses ressources et ses délais. Notion structure la gestion de projets BTP de façon simple et efficace.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Sans gestion de projet structurée, un artisan jongle entre les chantiers en cours, les devis à faire et les SAV à traiter, sans priorisation claire.',
      bullets: [
        'Plusieurs chantiers en cours sans vue d\'ensemble',
        'Tâches urgentes mélangées aux tâches importantes',
        'Pas de suivi des actions à faire par chantier'
      ]
    },
    notion: {
      title: 'Notion comme outil de gestion de projets BTP',
      description:
        'Notion transforme chaque chantier en projet structuré : étapes, tâches, responsables et jalons. Vous ne perdez plus rien et rien ne déborde.',
      bullets: [
        'Chaque chantier = un projet avec ses étapes',
        'Tâches priorisées et assignées par chantier',
        'Vue d\'ensemble de tous les projets en cours'
      ]
    },
    longForm: {
      intro: [
        "Un artisan qui gère bien ses projets livre dans les délais, dépasse rarement son budget et fidélise ses clients. La gestion de projet n'est pas réservée aux grandes entreprises : elle s'applique dès le premier chantier avec 2 artisans.",
        "Notion adapte les principes de gestion de projet à la réalité du BTP : simple, visuel, accessible depuis le terrain."
      ],
      sections: [
        {
          title: 'Structurer chaque chantier comme un projet',
          paragraphs: [
            "Un projet BTP, c'est une série d'étapes avec des dépendances : chaque phase doit être terminée avant que la suivante commence. Notion crée cette structure de façon claire.",
            "Chaque étape a un responsable, une date et un statut. Vous savez toujours où en est chaque chantier."
          ],
          bullets: [
            'Étapes de projet avec dépendances',
            'Responsable assigné à chaque étape',
            'Statut d\'avancement en temps réel'
          ]
        },
        {
          title: 'Prioriser les actions quotidiennes',
          paragraphs: [
            "Le matin, un artisan a besoin de savoir quoi faire en priorité. Notion crée une vue des tâches du jour, triées par chantier et par urgence.",
            "Les tâches administratives (devis à envoyer, factures à relancer) sont intégrées au même endroit que les tâches terrain."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template structure la gestion de projets BTP de bout en bout : de la prise de contact initiale jusqu'à la réception des travaux et le SAV. Chaque chantier est un projet géré avec rigueur.",
            "Il est utilisé par des artisans de tous corps de métier pour passer d'une gestion réactive à une gestion proactive de leur activité."
          ],
          bullets: [
            'Cycle de vie complet du projet : prospect → chantier → réception',
            'Tâches et sous-tâches par phase de chantier',
            'Vue quotidienne des actions prioritaires',
            'Réunions et comptes-rendus intégrés',
            'Bilan de chantier et retour d\'expérience'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template', 'gestion-projet-notion-guide-complet']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'gestion-projet-notion-guide-complet'],
    relatedPillarPaths: [
      '/gestion-chantier-artisan',
      '/outil-gestion-artisan-independant',
      '/organisation-entreprise-batiment',
      '/suivi-chantier-batiment',
      '/planning-chantier-batiment'
    ],
    seo: {
      title: 'Gestion Projets Artisan | Template Notion BTP',
      description:
        'Gérez vos projets BTP avec Notion : étapes de chantier, tâches priorisées, suivi d\'avancement. Template de gestion de projets pour artisans.',
      keywords: [
        'gestion projets artisan',
        'gestion de projet btp notion',
        'suivi projets artisan',
        'template notion gestion projet bâtiment',
        'piloter chantiers artisan'
      ],
      openGraph: {
        title: 'Gestion de projets artisan',
        description: 'Pilotez vos projets BTP de A à Z avec un template Notion adapté aux artisans.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un système de gestion de projets Notion pour votre métier',
      description: 'Quel que soit votre corps de métier, nous créons un template de gestion de projets adapté à votre activité.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Gestion projets artisan'
    },
    category: 'metier'
  },
  // ── Pages gestion et pilotage ────────────────────────────
  {
    id: 'suivi-budget-chantier',
    slug: 'suivi-budget-chantier',
    path: '/suivi-budget-chantier',
    title: 'Suivi budget chantier',
    subtitle:
      'Maîtrisez votre budget de chantier : devis, avenants, factures et reste à payer dans un tableau de bord clair.',
    description:
      'Un chantier qui dépasse son budget est souvent un chantier mal suivi financièrement. Notion crée un suivi budgétaire en temps réel pour chaque chantier.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Le dépassement de budget est l\'un des litiges les plus fréquents dans le BTP. Il vient rarement d\'une mauvaise estimation mais d\'un suivi financier inexistant en cours de chantier.',
      bullets: [
        'Avenants non trackés qui font exploser le budget',
        'Factures reçues sans comparaison avec les devis acceptés',
        'Aucune visibilité sur le reste à payer en fin de chantier'
      ]
    },
    notion: {
      title: 'Pourquoi Notion permet un suivi budgétaire rigoureux',
      description:
        'Notion relie devis, avenants et factures dans un tableau de bord budgétaire clair. Chaque chantier a son suivi financier en temps réel.',
      bullets: [
        'Budget initial, avenants et total engagé en un coup d\'œil',
        'Factures comparées aux devis lot par lot',
        'Reste à payer calculé automatiquement'
      ]
    },
    longForm: {
      intro: [
        "Le suivi budgétaire d'un chantier BTP est souvent négligé jusqu'à ce qu'il soit trop tard. Les avenants s'accumulent, les factures arrivent sans être rapprochées des devis et le client découvre un dépassement en fin de chantier. Résultat : un litige coûteux pour tout le monde.",
        "Notion permet de créer un suivi budgétaire simple et efficace pour chaque chantier. En 5 minutes par semaine, vous avez une vision claire de la santé financière de chaque projet."
      ],
      sections: [
        {
          title: 'Structurer le budget par lot de travaux',
          paragraphs: [
            "Un budget de chantier se structure par lot : maçonnerie, électricité, plomberie, menuiserie, finitions. Notion crée cette structure avec le budget alloué à chaque lot.",
            "Quand un avenant arrive sur un lot, il est immédiatement intégré et l'impact sur le budget global est visible."
          ],
          bullets: [
            'Budget initial par lot de travaux',
            'Avenants enregistrés et reliés au lot',
            'Budget révisé vs budget initial par lot'
          ]
        },
        {
          title: 'Rapprocher factures et devis',
          paragraphs: [
            "Chaque facture reçue doit être rapprochée du devis ou de l'avenant correspondant. Notion automatise ce rapprochement et alerte quand une facture dépasse le devis.",
            "Vous validez les paiements en connaissance de cause et avez toujours une trace de ce qui a été payé et pourquoi."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template inclut un module de suivi budgétaire complet par chantier : devis par lot, avenants, factures, règlements et reste à payer. Le tableau de bord financier est mis à jour en temps réel.",
            "Il est utilisé par des conducteurs de travaux et des maîtres d'ouvrage pour garder la maîtrise financière de leurs chantiers."
          ],
          bullets: [
            'Tableau de bord budgétaire par chantier',
            'Devis, avenants et factures reliés',
            'Reste à payer et reste à facturer',
            'Comparaison budget initial vs réalisé',
            'Historique de tous les paiements'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template'],
    relatedPillarPaths: [
      '/gestion-devis-chantier',
      '/suivi-travaux-maison',
      '/organiser-chantier-renovation',
      '/organisation-entreprise-batiment',
      '/gestion-chantier-architecte'
    ],
    seo: {
      title: 'Suivi Budget Chantier | Template Notion BTP',
      description:
        'Maîtrisez le budget de vos chantiers avec Notion : devis, avenants, factures et reste à payer. Template de suivi budgétaire chantier BTP.',
      keywords: [
        'suivi budget chantier',
        'suivi financier chantier btp',
        'budget chantier rénovation notion',
        'template suivi budget travaux',
        'contrôle budget chantier bâtiment'
      ],
      openGraph: {
        title: 'Suivi budget chantier',
        description: 'Gardez la maîtrise financière de vos chantiers avec un template Notion de suivi budgétaire.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un suivi budgétaire Notion adapté à vos chantiers',
      description: 'Conducteur de travaux, maître d\'ouvrage ou artisan : nous configurons le suivi budgétaire selon votre façon de travailler.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Suivi budget chantier'
    },
    category: 'metier'
  },
  {
    id: 'gestion-devis-chantier',
    slug: 'gestion-devis-chantier',
    path: '/gestion-devis-chantier',
    title: 'Gestion des devis chantier',
    subtitle:
      'Suivez vos devis BTP de l\'envoi à la signature : relances, comparatifs et pipeline commercial.',
    description:
      'Un devis non relancé est un chantier perdu. Notion crée un pipeline de devis BTP avec relances, comparatifs et suivi de la signature.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'La gestion des devis est le maillon faible de beaucoup d\'artisans et PME BTP : les devis partent mais ne sont jamais relancés systématiquement.',
      bullets: [
        'Devis envoyés par email, relances oubliées',
        'Taux d\'acceptation des devis inconnu',
        'Comparatif devis de sous-traitants fastidieux'
      ]
    },
    notion: {
      title: 'Pourquoi Notion améliore la gestion des devis',
      description:
        'Notion crée un pipeline commercial BTP clair : chaque devis a un statut, une date de relance et un lien vers le chantier correspondant.',
      bullets: [
        'Pipeline devis : envoyé, relancé, signé, refusé',
        'Date de relance automatique selon les délais',
        'Taux d\'acceptation et CA prévisionnel visibles'
      ]
    },
    longForm: {
      intro: [
        "La gestion des devis est une activité commerciale à part entière. Un artisan qui envoie 10 devis et n'en relance aucun perd des chantiers que ses concurrents moins compétents décrocheront juste parce qu'ils ont rappelé.",
        "Notion transforme la gestion des devis en un processus structuré : pipeline visuel, relances planifiées, taux de transformation mesurable. Voici comment mettre en place ce système."
      ],
      sections: [
        {
          title: 'Créer un pipeline de devis efficace',
          paragraphs: [
            "Un pipeline de devis en Kanban visualise immédiatement l'état de chaque devis : en préparation, envoyé, à relancer, signé, perdu. Vous savez où agir en priorité.",
            "Chaque devis est relié au client et au chantier potentiel. Si le devis est signé, le chantier est créé automatiquement avec le même contexte."
          ],
          bullets: [
            'Vue Kanban : en préparation → envoyé → signé',
            'Date d\'envoi et date de relance planifiée',
            'Montant et probabilité de signature'
          ]
        },
        {
          title: 'Gérer les devis de sous-traitants',
          paragraphs: [
            "Sur les gros chantiers, vous recevez des devis de plusieurs sous-traitants pour chaque lot. Notion crée un comparatif structuré : montant, délai, références, conditions.",
            "La décision est documentée, ce qui protège en cas de litige ultérieur sur le choix d'un sous-traitant."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template intègre un module complet de gestion des devis BTP : pipeline commercial, comparatif sous-traitants, relances et suivi de la facturation une fois le devis signé.",
            "C'est le lien entre votre activité commerciale et vos chantiers en cours, dans un seul espace cohérent."
          ],
          bullets: [
            'Pipeline commercial devis avec statuts',
            'Relances planifiées et historique des échanges',
            'Comparatif devis sous-traitants par lot',
            'Lien devis signé → chantier créé',
            'Taux d\'acceptation et CA prévisionnel'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template'],
    relatedPillarPaths: [
      '/suivi-budget-chantier',
      '/gestion-chantier-artisan',
      '/outil-gestion-artisan-independant',
      '/organisation-entreprise-batiment',
      '/suivi-artisans-chantier'
    ],
    seo: {
      title: 'Gestion Devis Chantier | Template Notion BTP',
      description:
        'Gérez vos devis BTP avec Notion : pipeline commercial, relances, comparatifs et suivi signature. Template de gestion des devis chantier.',
      keywords: [
        'gestion devis chantier',
        'suivi devis btp notion',
        'pipeline commercial artisan',
        'template notion devis chantier',
        'relance devis bâtiment'
      ],
      openGraph: {
        title: 'Gestion des devis chantier',
        description: 'Suivez vos devis BTP de l\'envoi à la signature avec un pipeline Notion structuré.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un pipeline de devis Notion pour votre activité BTP',
      description: 'Nous configurons votre pipeline commercial BTP selon votre processus de vente et votre volume de devis.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Gestion devis BTP'
    },
    category: 'metier'
  },
  {
    id: 'suivi-artisans-chantier',
    slug: 'suivi-artisans-chantier',
    path: '/suivi-artisans-chantier',
    title: 'Suivi des artisans sur chantier',
    subtitle:
      'Coordinatez les artisans, suivez leurs interventions et gérez les sous-traitants depuis un espace centralisé.',
    description:
      'Conducteurs de travaux et maîtres d\'œuvre : Notion centralise le suivi de tous les artisans intervenant sur vos chantiers, de la planification à la réception.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Coordonner plusieurs artisans sur un même chantier est un exercice d\'équilibriste. Une information qui ne passe pas et c\'est un retard en cascade.',
      bullets: [
        'Planning des artisans géré par appels téléphoniques',
        'Qualité des interventions non documentée',
        'Sous-traitants : contrats, assurances et Kbis non vérifiés systématiquement'
      ]
    },
    notion: {
      title: 'Pourquoi Notion structure la coordination des artisans',
      description:
        'Notion centralise le planning, les contacts et les documents de chaque artisan dans un espace accessible à toute l\'équipe de pilotage.',
      bullets: [
        'Planning d\'intervention de chaque artisan par chantier',
        'Fiche artisan avec contacts, assurances et qualifications',
        'Suivi de la qualité des interventions et réserves'
      ]
    },
    longForm: {
      intro: [
        "Sur un chantier de taille moyenne, 5 à 15 entreprises différentes peuvent intervenir. Coordonner leurs plannings, vérifier leurs qualifications, suivre la qualité de leurs interventions et gérer les réserves : c'est un travail à plein temps sans les bons outils.",
        "Notion permet de centraliser le suivi de tous les artisans d'un chantier dans un espace unique, accessible par toute l'équipe de pilotage."
      ],
      sections: [
        {
          title: 'Constituer un carnet d\'artisans fiable',
          paragraphs: [
            "Un bon conducteur de travaux s'appuie sur un réseau d'artisans qualifiés et fiables. Notion crée un carnet d'artisans structuré : corps de métier, zone d'intervention, tarifs habituels, qualité des interventions passées.",
            "Quand vous lancez un nouveau chantier, vous consultez ce carnet pour choisir les bons intervenants rapidement."
          ],
          bullets: [
            'Carnet d\'artisans classé par corps de métier',
            'Évaluation après chaque chantier',
            'Documents obligatoires : assurance décennale, Kbis'
          ]
        },
        {
          title: 'Planifier et coordonner les interventions',
          paragraphs: [
            "Le planning de coordination des artisans est le document le plus important d'un chantier. Notion le rend vivant et collaboratif : chaque artisan voit sa fenêtre d'intervention.",
            "Les dépendances entre corps de métier sont visibles. Un retard d'un artisan déclenche une réévaluation immédiate du planning des suivants."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template structure le suivi des artisans de façon professionnelle : carnet d'artisans avec qualifications, planning par chantier, comptes-rendus d'intervention et suivi des réserves.",
            "C'est l'outil de référence pour les conducteurs de travaux et maîtres d'œuvre qui veulent une coordination d'artisans sans faille."
          ],
          bullets: [
            'Carnet d\'artisans avec qualifications et évaluations',
            'Planning multi-artisans par chantier',
            'Suivi des interventions et réserves émises',
            'Documents administratifs sous-traitants centralisés',
            'Comptes-rendus de réunion de chantier'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template', 'pilotage-copropriete-chantiers'],
    relatedPillarPaths: [
      '/gestion-chantier-architecte',
      '/organiser-chantier-renovation',
      '/organisation-entreprise-batiment',
      '/suivi-chantier-batiment',
      '/planning-chantier-batiment'
    ],
    seo: {
      title: 'Suivi Artisans Chantier | Template Notion BTP',
      description:
        'Coordonnez les artisans sur vos chantiers avec Notion : planning, qualifications, réserves et comptes-rendus. Template suivi artisans BTP.',
      keywords: [
        'suivi artisans chantier',
        'coordination artisans btp notion',
        'gestion sous-traitants chantier',
        'template notion coordination artisans',
        'suivi interventions artisans'
      ],
      openGraph: {
        title: 'Suivi des artisans sur chantier',
        description: 'Coordonnez tous vos artisans et sous-traitants avec un template Notion structuré.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un espace Notion pour coordonner vos artisans',
      description: 'Conducteur de travaux, maître d\'œuvre ou maître d\'ouvrage : nous créons un système de coordination adapté à votre organisation.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Coordination artisans chantier'
    },
    category: 'metier'
  },
  // ── Pages rénovation / maison ────────────────────────────
  {
    id: 'organisation-renovation-maison',
    slug: 'organisation-renovation-maison',
    path: '/organisation-renovation-maison',
    title: 'Organisation rénovation maison',
    subtitle:
      'Planifiez, coordinatez et suivez votre projet de rénovation maison sans perdre le fil.',
    description:
      'Rénover sa maison est l\'un des projets les plus complexes à gérer pour un particulier. Notion structure le projet de rénovation de bout en bout : artisans, budget, planning et documents.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Un projet de rénovation maison non organisé génère du stress, des dépassements de budget et des retards. Le particulier se retrouve à gérer des dizaines d\'interlocuteurs sans système.',
      bullets: [
        'Devis de plusieurs artisans impossibles à comparer facilement',
        'Budget réel inconnu tant que les travaux ne sont pas finis',
        'Coordination des corps de métier laissée au hasard'
      ]
    },
    notion: {
      title: 'Pourquoi Notion aide à organiser une rénovation',
      description:
        'Notion centralise tous les aspects d\'un projet de rénovation maison : artisans, devis, planning, budget et documents garantie dans un espace structuré.',
      bullets: [
        'Comparatif des devis artisans par lot de travaux',
        'Planning des interventions avec dépendances',
        'Suivi du budget en temps réel'
      ]
    },
    longForm: {
      intro: [
        "Organiser une rénovation maison, c'est gérer un projet de plusieurs mois avec de nombreux intervenants, un budget souvent serré et des imprévus inévitables. Sans organisation, le projet dérive : les artisans se marchent dessus, le budget explose et les délais s'allongent.",
        "Notion permet au particulier de prendre le contrôle de son projet de rénovation avec les mêmes outils qu'un professionnel du BTP."
      ],
      sections: [
        {
          title: 'Préparer le projet de rénovation',
          paragraphs: [
            "La préparation est la phase la plus importante d'une rénovation. Notion crée un carnet de bord du projet : cahier des charges, liste des travaux par pièce, budget prévisionnel et planning cible.",
            "Vous entrez dans la phase des devis avec un cadre clair. Les artisans répondent à la même demande, ce qui rend les comparaisons pertinentes."
          ],
          bullets: [
            'Cahier des charges par pièce et par lot',
            'Budget prévisionnel avec marges d\'imprévus',
            'Planning cible avec phases et jalons'
          ]
        },
        {
          title: 'Sélectionner et coordonner les artisans',
          paragraphs: [
            "Choisir les bons artisans est crucial. Notion crée un tableau comparatif des devis reçus : montant, délai, références, conditions de paiement. Vous décidez sur des critères objectifs.",
            "Une fois les artisans sélectionnés, leur planning est intégré dans le planning général. Les dépendances sont visibles et les risques de retard anticipés."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template est utilisé par des particuliers pour organiser leur rénovation comme des professionnels. Du cahier des charges initial au dossier des ouvrages exécutés (DOE), il couvre tout le cycle de vie du projet.",
            "Avec ce template, vous n'êtes plus un simple particulier face aux artisans : vous êtes un maître d'ouvrage organisé qui suit son chantier avec rigueur."
          ],
          bullets: [
            'Cahier des charges et liste des travaux',
            'Comparatif devis artisans par lot',
            'Planning des interventions coordonné',
            'Suivi budgétaire en temps réel',
            'Dossier de fin de chantier : garanties et DOE'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template'],
    relatedPillarPaths: [
      '/organiser-chantier-renovation',
      '/suivi-travaux-maison',
      '/planning-travaux-renovation',
      '/suivi-budget-chantier',
      '/gestion-devis-chantier'
    ],
    seo: {
      title: 'Organisation Rénovation Maison | Template Notion BTP',
      description:
        'Organisez votre rénovation maison avec Notion : artisans, devis, planning et budget. Template pour particuliers qui rénovent leur maison.',
      keywords: [
        'organisation rénovation maison',
        'organiser travaux rénovation maison',
        'gérer rénovation maison notion',
        'template organisation rénovation',
        'planifier rénovation maison'
      ],
      openGraph: {
        title: 'Organisation rénovation maison',
        description: 'Prenez le contrôle de votre rénovation maison avec un template Notion structuré.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un template Notion pour piloter votre rénovation maison',
      description: 'Rénovation complète, cuisine, salle de bain ou extension : nous créons un template adapté à l\'ampleur de votre projet.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Organisation rénovation maison'
    },
    category: 'metier'
  },
  {
    id: 'planning-travaux-renovation',
    slug: 'planning-travaux-renovation',
    path: '/planning-travaux-renovation',
    title: 'Planning travaux rénovation',
    subtitle:
      'Créez un planning de rénovation réaliste, coordinatez les corps de métier et anticipez les retards.',
    description:
      'Un planning de travaux rénovation qui tient, c\'est la différence entre un chantier maîtrisé et un chantier qui déborde. Notion structure ce planning de façon collaborative.',
    heroImage: '/images/templates/gestion_chantiers.jpg',
    heroCta: {
      primary: { label: 'Voir le template Gestion de Chantiers', to: '/gestion-chantiers-reunions-commissions' },
      secondary: { label: 'Lire le guide BTP', to: '/blog/btp-gestion-chantiers-notion-template' }
    },
    problem: {
      title: 'Le problème',
      description:
        'Le planning de rénovation est souvent établi une fois et jamais mis à jour. Les retards s\'accumulent et personne n\'a de visibilité sur l\'impact global.',
      bullets: [
        'Planning fait sur tableur, partagé par email, vite obsolète',
        'Dépendances entre corps de métier non représentées',
        'Retard d\'un artisan = chaos sur le reste du chantier'
      ]
    },
    notion: {
      title: 'Pourquoi Notion rend le planning de rénovation vivant',
      description:
        'Notion crée un planning de rénovation mis à jour collaborativement, avec les dépendances entre lots et des alertes sur les retards critiques.',
      bullets: [
        'Planning par corps de métier avec dates et durées',
        'Dépendances entre lots : maçonnerie avant électricité, etc.',
        'Mise à jour en temps réel depuis le terrain'
      ]
    },
    longForm: {
      intro: [
        "Un chantier de rénovation sans planning rigoureux ressemble à un orchestre sans chef : chacun joue sa partition mais personne ne s'entend. Les plombiers arrivent quand les maçons n'ont pas encore terminé, l'électricien saigne les cloisons que le plaquiste venait de fermer.",
        "Un bon planning de travaux rénovation prévient ces situations. Notion permet de créer ce planning de façon simple, visuelle et collaborative, accessible depuis n'importe quel appareil."
      ],
      sections: [
        {
          title: 'Décomposer les travaux par phase',
          paragraphs: [
            "Un planning de rénovation commence par une décomposition logique : démolition, gros œuvre, réseaux (électricité et plomberie), isolation, plaquisterie, revêtements, finitions. Chaque phase a ses prérequis.",
            "Notion représente ces phases avec leurs durées et leurs dépendances. Vous visualisez le chemin critique : les phases qui, si elles prennent du retard, retardent tout le reste."
          ],
          bullets: [
            'Phases de rénovation avec durées estimées',
            'Chemin critique identifié visuellement',
            'Marge de sécurité intégrée à chaque phase'
          ]
        },
        {
          title: 'Intégrer les contraintes de chaque artisan',
          paragraphs: [
            "Chaque artisan a ses contraintes de planning : disponibilité, délais d'approvisionnement, temps de séchage. Notion intègre ces contraintes dans le planning global.",
            "Vous êtes informé immédiatement quand un artisan annonce un retard et vous pouvez réévaluer l'impact sur les phases suivantes."
          ]
        },
        {
          title: 'Template Notion Gestion de Chantiers – Réunions & Commissions',
          paragraphs: [
            "Ce template inclut un module de planning de travaux rénovation avec phases, dépendances, intervenants et suivi d'avancement. Il est conçu pour être mis à jour facilement au fil du chantier.",
            "Que vous soyez maître d'ouvrage particulier ou conducteur de travaux professionnel, ce planning vous donne la maîtrise de votre rénovation."
          ],
          bullets: [
            'Planning rénovation par phase et par corps de métier',
            'Dépendances entre phases représentées',
            'Avancement en pourcentage par phase',
            'Alertes sur les retards et impacts',
            'Historique des révisions du planning'
          ]
        }
      ],
      templateLinkIds: ['gestion-chantiers-reunions-commissions'],
      articleLinkSlugs: ['btp-gestion-chantiers-notion-template']
    },
    templateIds: ['gestion-chantiers-reunions-commissions'],
    articleSlugs: ['btp-gestion-chantiers-notion-template'],
    relatedPillarPaths: [
      '/organiser-chantier-renovation',
      '/organisation-renovation-maison',
      '/suivi-travaux-maison',
      '/planning-chantier-batiment',
      '/suivi-artisans-chantier'
    ],
    seo: {
      title: 'Planning Travaux Rénovation | Template Notion BTP',
      description:
        'Créez un planning de travaux rénovation rigoureux avec Notion : phases, corps de métier, dépendances et alertes retard. Template planning rénovation.',
      keywords: [
        'planning travaux rénovation',
        'planning rénovation maison notion',
        'planifier travaux rénovation',
        'template planning travaux',
        'planning chantier rénovation'
      ],
      openGraph: {
        title: 'Planning travaux rénovation',
        description: 'Créez un planning de rénovation rigoureux avec Notion : phases, dépendances et suivi d\'avancement.',
        type: 'website',
        image: '/images/templates/gestion_chantiers.jpg'
      }
    },
    customCta: {
      title: 'Un planning de travaux Notion adapté à votre rénovation',
      description: 'Rénovation légère ou lourde, petite surface ou grande maison : nous créons un planning adapté à votre projet.',
      buttonText: 'Demander un template sur mesure',
      contactSubject: 'Template sur mesure - Planning travaux rénovation'
    },
    category: 'metier'
  }
];
